import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CH } from './lamps';
import { kitPart, onKitParts } from './packGeometry';
import { lineY, loftShell, panelLoft, sweepCabin, sweepRect, type Arch, type Pt, type SectionStyle, type Shell } from './shell';
import type { VehicleBodyStyle, VehicleSpec } from './types';

/**
 * Procedural vehicle meshes, third pass.
 *
 * Every car is ONE merged, vertex-coloured body mesh (paint, chrome, rubber, lenses, plates, shut
 * lines, interior and the seated driver) plus one glass mesh; wheels are instanced across all cars
 * (wheelBatch.ts). A `surf` vec4 attribute carries (paint slot, roughness, metalness, channel): the
 * car shader (carShader.ts) uses it to paint per-car colours, light lamps, hang loose parts and show
 * the driver, all without extra draw calls (docs/PERF.md "merge per-car parts" recommendation).
 *
 * Since the third pass the lower body is a sculpted loft (shell.ts): crowned bonnet and deck, a
 * rounded shoulder, a swage crease, tuck-under at the sills, eyebrowed wheel arches with liners, and
 * rounded ends in plan. The greenhouse is a sweep with curved screens and a cambered roof. Trim
 * (shut lines, waist chrome, sills) follows the sculpted surface. Three bodies per model: HIGH (full
 * sculpt), MID (coarser loft, no small parts; also the near body on MEDIUM/LOW quality) and FAR (a
 * ~300-triangle impostor). All makes are original; shapes lean on late-60s British proportions.
 *
 * Local frame: +Z forward, +X = vehicle LEFT, +Y up, origin at ground level under the body centre.
 * Collision/physics never read any of this.
 */

/**
 * The first eight are procedural (wheelGeometry); the last six are Pike's premium rims, modelled in Blender
 * (src/vehicles/rimModels.ts; shared/garage.ts RIMS) with a procedural stand-in here until their pack is in.
 */
export type WheelStyle = 'hubcap' | 'chrome' | 'wire' | 'steel' | 'disc' | 'truck' | 'minilite' | 'rostyle' | 'lattice' | 'forged' | 'ninestar' | 'twelve' | 'twinspoke' | 'honeycomb';
export const WHEEL_STYLE_IDS: readonly WheelStyle[] = ['hubcap', 'chrome', 'wire', 'steel', 'disc', 'truck', 'minilite', 'rostyle', 'lattice', 'forged', 'ninestar', 'twelve', 'twinspoke', 'honeycomb'];

export interface WheelPlacement {
  pos: THREE.Vector3;
  radius: number;
  width: number;
  steer: boolean;
  /** +1 on the left flank (+X), -1 on the right. */
  side: 1 | -1;
}

export interface VehicleParts {
  /** Merged detailed body (attributes: position, normal, uv, color, surf). */
  body: THREE.BufferGeometry;
  /** Mid body: coarser shell without small parts, interior and driver (MEDIUM/LOW near body too). */
  mid: THREE.BufferGeometry;
  /** Distance LOD body: silhouette, roof, dark glass and lamps only. */
  far: THREE.BufferGeometry;
  glass: THREE.BufferGeometry;
  /** Cheaper glass for the mid body. */
  glassMid: THREE.BufferGeometry;
  /** Additive road/air light (attributes: position, color, lch). */
  lights: THREE.BufferGeometry;
  wheelStyle: WheelStyle;
  wheels: WheelPlacement[];
  /** Hinge (z, y) of the bonnet / boot lid, null if the body has none. */
  bonnetHinge: [number, number] | null;
  bootHinge: [number, number] | null;
  /** Bumper centre (z, y) and half width, front and rear. */
  bumperF: [number, number, number];
  bumperR: [number, number, number];
  /** Blue lamp centre (police), null otherwise. */
  beacon: THREE.Vector3 | null;
  /** Local tail-pipe outlet(s) for exhaust smoke. */
  exhaust: THREE.Vector3[];
  wheelRadius: number;
  /** Height of the body's centre for smoke/fire emitters. */
  engineHeight: number;
  /** Arch rust uniform: (zFront, zRear, cy, r). */
  arch: [number, number, number, number];
  clearance: number;
  /** Triangle count of the detailed body (for budgets/tests). */
  triangles: number;
  /** Triangle counts of the mid and far bodies. */
  trianglesMid: number;
  trianglesFar: number;
  /** Belt (base of the side glass) and roof height of the greenhouse (m). */
  beltY: number;
  roofY: number;
  /** z of the base of the rear screen and of the windscreen. */
  screens: [number, number];
  /**
   * Labelled groups of the detailed body, kept as their own geometries for a detailed (Meshy) body that takes
   * them over (meshyCar.ts): 'cabin' (seats, dash, steering wheel and the driver), 'plateF' / 'plateR' (plate
   * backings and glyph faces), 'police' (the blue lamp and the roof sign), 'roof' (the roof / ladder rack and the
   * cab's TAXI sign). `mid` is null where the group has no mid version.
   */
  extras: Partial<Record<ExtraLabel, { full: THREE.BufferGeometry; mid: THREE.BufferGeometry | null }>>;
}

export type ExtraLabel = 'cabin' | 'plateF' | 'plateR' | 'police' | 'roof';

// ------------------------------------------------------------------------------------ surfaces

interface Surf {
  col?: number;
  /** 0 none, 1 body paint, 2 optional secondary paint (stripes/band/bed), 3 upholstery, 4 driver coat, 5 driver skin. */
  slot?: number;
  rough?: number;
  metal?: number;
  ch?: number;
  /** Glyph face: 1 = number plate (uv v shifted by 2), 2 = roof sign (v shifted by 4), 3 = lamp lens (v shifted by 6). */
  glyph?: 1 | 2 | 3;
}

const PAINT: Surf = { slot: 1, rough: 0.42, metal: 0.18 };
const PAINT2: Surf = { slot: 2, rough: 0.42, metal: 0.15 };
const CHROME: Surf = { col: 0xd6d3cc, rough: 0.14, metal: 1 };
const DULL_CHROME: Surf = { col: 0x9a9890, rough: 0.35, metal: 0.9 };
const RUBBER: Surf = { col: 0x121212, rough: 0.92 };
const BLACK: Surf = { col: 0x191919, rough: 0.6 };
const SEAM: Surf = { col: 0x0a0908, rough: 0.95 };
const DARK: Surf = { col: 0x0c0c0d, rough: 0.9 };
const SEAL: Surf = { col: 0x101010, rough: 0.85 };
const UPHOL: Surf = { slot: 3, rough: 0.7 };
const COAT: Surf = { slot: 4, rough: 0.85, ch: CH.DRIVER };
const SKIN: Surf = { slot: 5, rough: 0.7, ch: CH.DRIVER };
const HAIR: Surf = { col: 0x1d1611, rough: 0.9, ch: CH.DRIVER };
const lens = (col: number, ch: number): Surf => ({ col, rough: 0.12, metal: 0.25, ch });
const HEAD: Surf = { ...lens(0xf2eee2, CH.HEAD), glyph: 3 };
/** Headlamp block on the far impostor: no lens fluting. */
const HEAD_FAR: Surf = lens(0xf2eee2, CH.HEAD);
const TAIL = lens(0xa3180f, CH.TAIL);
const REV = lens(0xe6e4dc, CH.REVERSE);
const IND = (ch: number): Surf => lens(0xe08a1a, ch);
const PLATE: Surf = { col: 0x050505, rough: 0.6, glyph: 1 };

const _c = new THREE.Color();

function ensureNonIndexed(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g;
  if (out !== g) {
    out.userData = { ...g.userData };
    g.dispose();
  }
  if (!out.getAttribute('uv')) out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(out.getAttribute('position').count * 2), 2));
  if (!out.getAttribute('normal')) out.computeVertexNormals();
  return out;
}

function tag(g: THREE.BufferGeometry, s: Surf): THREE.BufferGeometry {
  const smooth = g.userData.smooth === true;
  const sculpted = g.userData.sculpted === true;
  const out = ensureNonIndexed(g);
  out.userData.smooth = smooth;
  out.userData.sculpted = sculpted;
  // UVs only matter on glyph faces (plates / signs / lenses), flagged by the v offset; zero them elsewhere.
  const uv = out.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    if (s.glyph) uv.setY(i, uv.getY(i) + s.glyph * 2);
    else uv.setXY(i, 0, 0);
  }
  for (const name of Object.keys(out.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') out.deleteAttribute(name);
  const n = out.getAttribute('position').count;
  const col = new Float32Array(n * 3);
  const surf = new Float32Array(n * 4);
  _c.setHex(s.col ?? 0xffffff);
  for (let i = 0; i < n; i++) {
    col[i * 3] = _c.r;
    col[i * 3 + 1] = _c.g;
    col[i * 3 + 2] = _c.b;
    surf[i * 4] = s.slot ?? 0;
    surf[i * 4 + 1] = s.rough ?? 0.5;
    surf[i * 4 + 2] = s.metal ?? 0;
    surf[i * 4 + 3] = s.ch ?? 0;
  }
  out.setAttribute('color', new THREE.BufferAttribute(col, 3));
  out.setAttribute('surf', new THREE.BufferAttribute(surf, 4));
  return out;
}

/**
 * Remove zero-area triangles (lofts with collapsed points and densified outlines leave plenty). They
 * cost vertex work for nothing, and some rasterisers interpolate garbage across them under MSAA.
 */
function dropDegenerate(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = g.getAttribute('position');
  const n = pos.count;
  const keep: number[] = [];
  for (let i = 0; i < n; i += 3) {
    const ax = pos.getX(i), ay = pos.getY(i), az = pos.getZ(i);
    const ux = pos.getX(i + 1) - ax, uy = pos.getY(i + 1) - ay, uz = pos.getZ(i + 1) - az;
    const vx = pos.getX(i + 2) - ax, vy = pos.getY(i + 2) - ay, vz = pos.getZ(i + 2) - az;
    const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
    if (cx * cx + cy * cy + cz * cz > 1e-14) keep.push(i);
  }
  if (keep.length * 3 === n) return g;
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(g.attributes)) {
    const src = g.getAttribute(name) as THREE.BufferAttribute;
    const size = src.itemSize;
    const arr = new Float32Array(keep.length * 3 * size);
    let o = 0;
    for (const t of keep) for (let k = 0; k < 3 * size; k++) arr[o++] = (src.array as Float32Array)[t * size + k]!;
    out.setAttribute(name, new THREE.BufferAttribute(arr, size));
  }
  g.dispose();
  return out;
}

/** Channels that must survive in the mid-distance LOD even on small parts. */
const KEEP_MID = new Set<number>([CH.HEAD, CH.TAIL, CH.BEACON, CH.SIGN, CH.ROOF]);

/** `both` keeps a small part (lamp bowl, bezel, pod) in the mid body instead of dropping it as fine detail. */
type Lod = 'full' | 'mid' | 'both';

class Kit {
  readonly parts: THREE.BufferGeometry[] = [];
  /** While true, added parts are interior/driver-level detail dropped from the mid LOD. */
  fine = false;
  /** While set, added parts are also collected into this labelled group (VehicleParts.extras). */
  label: ExtraLabel | null = null;
  /** Labelled groups from the last buildLod. */
  labelled: VehicleParts['extras'] = {};
  /** Add a part; `lod` restricts it to one body (the detailed or the mid one), or forces it into both. */
  add(g: THREE.BufferGeometry, s: Surf, lod?: Lod): this {
    const t = tag(g, s);
    t.userData.label = this.label;
    let fine = this.fine;
    if (!fine && !KEEP_MID.has(s.ch ?? 0) && !lod) {
      t.computeBoundingBox();
      const b = t.boundingBox!;
      fine = Math.max(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) < 0.25;
    }
    t.userData.fine = fine;
    t.userData.lod = lod;
    this.parts.push(t);
    return this;
  }
  /** A Blender part from the wheels pack (packGeometry.ts kitPart: already in Kit form, its own colours and
   * channels per vertex), as it is. Never fine detail: the mid body keeps the lamp as the procedural one does. */
  addBaked(g: THREE.BufferGeometry, lod?: Lod): this {
    g.userData.label = this.label;
    g.userData.smooth = true;
    g.userData.sculpted = false;
    g.userData.fine = false;
    g.userData.lod = lod;
    this.parts.push(g);
    return this;
  }
  /** Same part on both flanks (x mirrored); `make(sx)` builds the part for side sx = ±1. */
  both(make: (sx: 1 | -1) => THREE.BufferGeometry, s: Surf | ((sx: 1 | -1) => Surf), lod?: Lod): this {
    for (const sx of [1, -1] as const) this.add(make(sx), typeof s === 'function' ? s(sx) : s, lod);
    return this;
  }
  build(plan?: PlanShape): THREE.BufferGeometry {
    return this.buildLod(plan).full;
  }
  /** Full detail plus a mid-distance version without small parts, interior and driver. */
  buildLod(plan?: PlanShape): { full: THREE.BufferGeometry; mid: THREE.BufferGeometry } {
    if (plan) for (const p of this.parts) if (!p.userData.sculpted) planShape(p, plan, !p.userData.smooth);
    const fullParts = this.parts.filter((p) => p.userData.lod !== 'mid');
    const full = dropDegenerate(mergeGeometries(fullParts)!);
    const coarse = this.parts.filter((p) => p.userData.lod !== 'full' && !p.userData.fine);
    const mid = dropDegenerate(mergeGeometries(coarse.length ? coarse : fullParts)!);
    this.labelled = {};
    for (const label of new Set(this.parts.map((p) => p.userData.label as ExtraLabel | null))) {
      if (!label) continue;
      const lf = fullParts.filter((p) => p.userData.label === label);
      const lm = coarse.filter((p) => p.userData.label === label);
      this.labelled[label] = { full: mergeGeometries(lf)!, mid: lm.length ? mergeGeometries(lm)! : null };
    }
    for (const p of this.parts) p.dispose();
    this.parts.length = 0;
    return { full, mid };
  }
}

// ------------------------------------------------------------------------------------ primitives

function box(w: number, h: number, l: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, l);
  g.translate(x, y, z);
  return g;
}

/** A box of cross-section (wx × t) running from (y0,z0) to (y1,z1) in the side plane at x. */
function beam(x: number, z0: number, y0: number, z1: number, y1: number, wx: number, t: number): THREE.BufferGeometry {
  const len = Math.hypot(z1 - z0, y1 - y0);
  const g = new THREE.BoxGeometry(wx, len, t);
  g.rotateX(Math.atan2(z1 - z0, y1 - y0));
  g.translate(x, (y0 + y1) / 2, (z0 + z1) / 2);
  return g;
}

/**
 * A pillar along a greenhouse edge: like `beam`, but pushed into the glass so only ~1.5 cm stands
 * proud of the screen (side = +1 pushes toward +normal of the rear edge, -1 for the front edge).
 */
function edgeBeam(x: number, z0: number, y0: number, z1: number, y1: number, wx: number, t: number, side: 1 | -1): THREE.BufferGeometry {
  const len = Math.hypot(z1 - z0, y1 - y0) || 1;
  const nz = ((y1 - y0) / len) * side;
  const ny = (-(z1 - z0) / len) * side;
  const off = Math.max(0, t / 2 - 0.015);
  return beam(x, z0 + nz * off, y0 + ny * off, z1 + nz * off, y1 + ny * off, wx, t);
}

/** Cylinder whose axis is Z (lamp lens / pipe), front face at z + facing*depth. */
function discZ(r: number, depth: number, x: number, y: number, z: number, facing: 1 | -1, seg = 12): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, depth, seg);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, z + (facing * depth) / 2);
  g.userData.smooth = true;
  return g;
}

const BOWL: Surf = { col: 0x1a1a1c, rough: 0.6, metal: 0.4 };

/**
 * A headlamp: a slightly domed lens (its cap uv carries the radial fluting) in a chrome bezel with a
 * dark bowl behind. Bowl and bezel stay on the mid body (a cheaper bezel) so MEDIUM lamps don't
 * collapse back into flat white discs.
 */
function headlamp(kit: Kit, r: number, x: number, y: number, z: number, facing: 1 | -1, seg = 14): void {
  kit.add(discZ(r * 1.1, 0.06, x, y, z - facing * 0.06, facing, seg), BOWL, 'both');
  kit.add(discZ(r, 0.03, x, y, z, facing, seg), HEAD);
  kit.add(ringZ(r * 1.04, r * 0.16, x, y, z + facing * 0.012, seg), CHROME, 'full');
  kit.add(ringZ(r * 1.04, r * 0.16, x, y, z + facing * 0.012, 10, 3), CHROME, 'mid');
}

/** Ring (torus) around Z, for chrome lamp bezels. */
function ringZ(r: number, tube: number, x: number, y: number, z: number, seg = 12, radial = 4): THREE.BufferGeometry {
  const g = new THREE.TorusGeometry(r, tube, radial, seg);
  g.translate(x, y, z);
  g.userData.smooth = true;
  return g;
}

function cylX(r: number, len: number, x: number, y: number, z: number, seg = 10): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg);
  g.rotateZ(Math.PI / 2);
  g.translate(x, y, z);
  g.userData.smooth = true;
  return g;
}

function cylY(r0: number, r1: number, h: number, x: number, y: number, z: number, seg = 10): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, h, seg);
  g.translate(x, y + h / 2, z);
  g.userData.smooth = true;
  return g;
}

function sphere(r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 8): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(r, seg, Math.max(4, seg - 2));
  g.scale(sx, sy, sz);
  g.translate(x, y, z);
  g.userData.smooth = true;
  return g;
}

/** Flat quad facing +Z (or -Z), uv 0..1 left→right as seen from the front of that face. */
function plateQuad(w: number, h: number, x: number, y: number, z: number, facing: 1 | -1): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, h);
  if (facing < 0) g.rotateY(Math.PI);
  g.translate(x, y, z);
  return g;
}

const BEVEL = { bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.045, bevelSegments: 2, curveSegments: 6 } as const;

function shapeFrom(points: Pt[]): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(points[0]![0], points[0]![1]);
  for (let i = 1; i < points.length; i++) s.lineTo(points[i]![0], points[i]![1]);
  s.closePath();
  return s;
}

/** Extrude a side outline (z,y in metres) across `width`, centred on x. */
function extrudeSide(points: Pt[], width: number, bevel = true, x = 0): THREE.BufferGeometry {
  const b = bevel ? BEVEL : { bevelEnabled: false, curveSegments: 6 };
  const depth = Math.max(0.01, width - (bevel ? BEVEL.bevelThickness * 2 : 0));
  const g = new THREE.ExtrudeGeometry(shapeFrom(points), { depth, ...b });
  g.rotateY(-Math.PI / 2);
  g.translate(depth / 2 + x, 0, 0);
  return g;
}

/** Split long edges so plan shaping can bend the flanks smoothly. */
function densify(points: Pt[], maxSeg: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    out.push(a);
    const n = Math.floor(Math.hypot(b[0] - a[0], b[1] - a[1]) / maxSeg);
    for (let k = 1; k <= n; k++) {
      const t = k / (n + 1);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}

/** Dark inner liner so you never see into an extruded body through its arch. */
function archLiner(a: Arch, width: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(a.r - 0.005, a.r - 0.005, width, 14, 1, true, 0, Math.PI);
  g.rotateZ(Math.PI / 2);
  g.translate(0, a.cy, a.z);
  const idx = g.index!;
  for (let i = 0; i < idx.count; i += 3) {
    const t = idx.getX(i + 1);
    idx.setX(i + 1, idx.getX(i + 2));
    idx.setX(i + 2, t);
  }
  const nrm = g.getAttribute('normal');
  for (let i = 0; i < nrm.count; i++) nrm.setXYZ(i, -nrm.getX(i), -nrm.getY(i), -nrm.getZ(i));
  g.userData.smooth = true;
  return g;
}

/** Half-tube arch lip around a wheel arch on one flank (used where the arch is in an extruded wing). */
function archLip(a: Arch, x: number, tube: number): THREE.BufferGeometry {
  const g = new THREE.TorusGeometry(a.r + tube * 0.4, tube, 4, 14, Math.PI);
  g.rotateY(Math.PI / 2);
  g.translate(x, a.cy, a.z);
  g.userData.smooth = true;
  return g;
}

/** A vertical bead on the flank at z (door shut line) that follows the sculpted side. */
function flankStrip(shell: Shell, sx: 1 | -1, z: number, y0: number, y1: number, proud: number, halfT: number, halfW: number, n = 6): THREE.BufferGeometry {
  const pts: number[][] = [];
  for (let k = 0; k <= n; k++) {
    const y = y0 + ((y1 - y0) * k) / n;
    pts.push([sx * (shell.flankX(z, y) + proud), y, z]);
  }
  return sweepRect(pts, [halfT, 0, 0], [0, 0, halfW]);
}

/** A horizontal bead along the flank at height y (waist chrome, sill, rubbing strip). */
function beltBead(shell: Shell, sx: 1 | -1, y: number, z0: number, z1: number, proud: number, halfT: number, halfH: number, n = 10): THREE.BufferGeometry {
  const pts: number[][] = [];
  for (let k = 0; k <= n; k++) {
    const z = z0 + ((z1 - z0) * k) / n;
    pts.push([sx * (shell.flankX(z, y) + proud), y, z]);
  }
  return sweepRect(pts, [halfT, 0, 0], [0, halfH, 0]);
}

/**
 * Plan shaping for parts: pinch the ends (rounded corners from above) and pull the greenhouse in
 * above the belt line (tumblehome). Lofted pieces include the pinch already and skip this.
 */
interface PlanShape {
  halfL: number;
  front: number;
  rear: number;
  round: number;
  beltY: number;
  roofY: number;
  tumble: number;
}

function pinchFn(p: PlanShape): (z: number) => number {
  const zs = Math.max(0.1, p.halfL - p.round);
  return (z) => {
    const t = Math.min(1, Math.max(0, (Math.abs(z) - zs) / (p.halfL - zs)));
    return 1 - (z > 0 ? p.front : p.rear) * t * t;
  };
}

function planShape(g: THREE.BufferGeometry, p: PlanShape, recompute = true): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const nrm = g.getAttribute('normal') as THREE.BufferAttribute;
  const pinch = pinchFn(p);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const u = Math.min(1, Math.max(0, (y - p.beltY) / Math.max(0.1, p.roofY - p.beltY)));
    const k = pinch(z) * (1 - p.tumble * u);
    pos.setX(i, x * k);
    if (!recompute) {
      const nx = nrm.getX(i) / k;
      const ny = nrm.getY(i);
      const nz = nrm.getZ(i);
      const len = Math.hypot(nx, ny, nz) || 1;
      nrm.setXYZ(i, nx / len, ny / len, nz / len);
    }
  }
  pos.needsUpdate = true;
  if (recompute) g.computeVertexNormals();
}

// ------------------------------------------------------------------------------------ car designs

interface Sculpt {
  corner: number;
  tuck: number;
  /** Fraction of H. */
  tuckTop: number;
  /** Fraction of H, or null. */
  swageY: number | null;
  swage: number;
  crown: number;
  flare: number;
  /** Plan curvature of the screens and camber of the roof (m). */
  screenCrown: number;
  roofCrown: number;
}

interface Design {
  /** Body top line rear→front as (zFrac -0.5..0.5, yFrac of height). */
  lower: Pt[];
  /** Greenhouse: rear base, rear roof, front roof, front base (fractions). */
  cabin: Pt[];
  cabinWidth: number;
  roof: [number, number] | null;
  clearance: number;
  wheelR: number;
  wheelStyle: WheelStyle;
  /** Bonnet / boot z ranges (fractions) on the top line, null if none. */
  bonnet: [number, number] | null;
  boot: [number, number] | null;
  plan: { front: number; rear: number; round: number; tumble: number };
  /** Two-door cars have no B-pillar seam. */
  doors: 2 | 4;
  sculpt: Sculpt;
}

const DESIGNS: Record<Exclude<VehicleBodyStyle, 'lorry'>, Design> = {
  hatch: {
    // Austral Minnow: a small two-box saloon, wheels at the corners, tall glasshouse, clamshell bonnet.
    lower: [
      [-0.5, 0.4],
      [-0.497, 0.58],
      [-0.48, 0.625],
      [-0.44, 0.64],
      [0.2, 0.645],
      [0.26, 0.625],
      [0.44, 0.59],
      [0.485, 0.555],
      [0.5, 0.44],
    ],
    cabin: [
      [-0.455, 0.625],
      [-0.425, 0.965],
      [0.075, 0.975],
      [0.2, 0.635],
    ],
    cabinWidth: 0.86,
    roof: [-0.42, 0.07],
    clearance: 0.16,
    wheelR: 0.27,
    wheelStyle: 'hubcap',
    bonnet: [0.205, 0.47],
    boot: null,
    plan: { front: 0.1, rear: 0.06, round: 0.5, tumble: 0.1 },
    doors: 2,
    sculpt: { corner: 0.085, tuck: 0.035, tuckTop: 0.3, swageY: null, swage: 0, crown: 0.025, flare: 0.012, screenCrown: 0.045, roofCrown: 0.03 },
  },
  saloon: {
    // Halberd Regent: a long executive saloon with a strong shoulder crease and a hint of fins.
    lower: [
      [-0.5, 0.47],
      [-0.498, 0.6],
      [-0.48, 0.635],
      [-0.44, 0.64],
      [-0.29, 0.64],
      [0.19, 0.64],
      [0.25, 0.628],
      [0.47, 0.6],
      [0.495, 0.56],
      [0.5, 0.46],
    ],
    cabin: [
      [-0.29, 0.635],
      [-0.18, 0.955],
      [0.05, 0.965],
      [0.19, 0.635],
    ],
    cabinWidth: 0.85,
    roof: [-0.175, 0.045],
    clearance: 0.2,
    wheelR: 0.33,
    wheelStyle: 'chrome',
    bonnet: [0.195, 0.475],
    boot: [-0.48, -0.295],
    plan: { front: 0.06, rear: 0.045, round: 0.6, tumble: 0.14 },
    doors: 4,
    sculpt: { corner: 0.065, tuck: 0.05, tuckTop: 0.33, swageY: 0.5, swage: 0.01, crown: 0.022, flare: 0.014, screenCrown: 0.05, roofCrown: 0.028 },
  },
  coupe: {
    // Vesper GT: low and wide, long bonnet, deep tuck-under, fastback.
    lower: [
      [-0.5, 0.45],
      [-0.497, 0.58],
      [-0.47, 0.61],
      [-0.42, 0.62],
      [0.02, 0.62],
      [0.1, 0.6],
      [0.3, 0.555],
      [0.46, 0.49],
      [0.495, 0.43],
      [0.5, 0.36],
    ],
    cabin: [
      [-0.43, 0.6],
      [-0.17, 0.96],
      [-0.02, 0.97],
      [0.1, 0.6],
    ],
    cabinWidth: 0.8,
    roof: [-0.16, -0.03],
    clearance: 0.16,
    wheelR: 0.33,
    wheelStyle: 'wire',
    bonnet: [0.105, 0.455],
    boot: null,
    plan: { front: 0.15, rear: 0.08, round: 0.85, tumble: 0.18 },
    doors: 2,
    sculpt: { corner: 0.1, tuck: 0.06, tuckTop: 0.3, swageY: 0.4, swage: 0.008, crown: 0.03, flare: 0.016, screenCrown: 0.06, roofCrown: 0.03 },
  },
  cab: {
    // Halberd Hackney: upright tub, separate flowing wings, tall grille.
    lower: [
      [-0.5, 0.36],
      [-0.495, 0.5],
      [-0.46, 0.53],
      [0.21, 0.53],
    ],
    cabin: [
      [-0.455, 0.52],
      [-0.42, 0.965],
      [0.12, 0.97],
      [0.2, 0.52],
    ],
    cabinWidth: 0.92,
    roof: [-0.41, 0.11],
    clearance: 0.22,
    wheelR: 0.33,
    wheelStyle: 'disc',
    bonnet: null,
    boot: null,
    plan: { front: 0.08, rear: 0.06, round: 0.45, tumble: 0.06 },
    doors: 4,
    sculpt: { corner: 0.05, tuck: 0.02, tuckTop: 0.28, swageY: null, swage: 0, crown: 0.015, flare: 0.01, screenCrown: 0.03, roofCrown: 0.045 },
  },
  van: {
    // Corran Haulmaster: a box van with a short nose.
    lower: [
      [-0.5, 0.975],
      [0.235, 0.975],
      [0.245, 0.93],
      [0.25, 0.6],
      [0.43, 0.57],
      [0.49, 0.48],
      [0.5, 0.36],
    ],
    cabin: [
      [0.2, 0.59],
      [0.2, 0.935],
      [0.255, 0.935],
      [0.42, 0.58],
    ],
    cabinWidth: 0.94,
    roof: null,
    clearance: 0.24,
    wheelR: 0.36,
    wheelStyle: 'steel',
    bonnet: [0.255, 0.485],
    boot: null,
    plan: { front: 0.07, rear: 0.02, round: 0.45, tumble: 0 },
    doors: 2,
    sculpt: { corner: 0.05, tuck: 0.012, tuckTop: 0.22, swageY: null, swage: 0, crown: 0.03, flare: 0.012, screenCrown: 0.04, roofCrown: 0 },
  },
  estate: {
    // Corran Drover Estate: the saloon's lower body with the roof carried straight back to an upright tailgate.
    lower: [
      [-0.5, 0.45],
      [-0.498, 0.58],
      [-0.488, 0.62],
      [-0.47, 0.625],
      [0.19, 0.625],
      [0.25, 0.612],
      [0.47, 0.585],
      [0.495, 0.55],
      [0.5, 0.45],
    ],
    cabin: [
      [-0.485, 0.62],
      [-0.47, 0.955],
      [0.05, 0.965],
      [0.19, 0.62],
    ],
    cabinWidth: 0.86,
    roof: [-0.465, 0.045],
    clearance: 0.19,
    wheelR: 0.32,
    wheelStyle: 'steel',
    bonnet: [0.195, 0.475],
    boot: null,
    plan: { front: 0.06, rear: 0.03, round: 0.55, tumble: 0.1 },
    doors: 4,
    sculpt: { corner: 0.06, tuck: 0.045, tuckTop: 0.33, swageY: 0.5, swage: 0.01, crown: 0.022, flare: 0.012, screenCrown: 0.045, roofCrown: 0.026 },
  },
  exec: {
    // Ashcombe Grandee: a long, low limousine-saloon: a very long bonnet, the cabin set back, a long flat boot.
    lower: [
      [-0.5, 0.46],
      [-0.498, 0.6],
      [-0.48, 0.635],
      [-0.44, 0.64],
      [-0.3, 0.64],
      [0.15, 0.64],
      [0.21, 0.63],
      [0.47, 0.605],
      [0.495, 0.565],
      [0.5, 0.46],
    ],
    cabin: [
      [-0.3, 0.635],
      [-0.2, 0.95],
      [0.02, 0.96],
      [0.15, 0.635],
    ],
    cabinWidth: 0.84,
    roof: [-0.195, 0.015],
    clearance: 0.18,
    wheelR: 0.34,
    wheelStyle: 'chrome',
    bonnet: [0.155, 0.475],
    boot: [-0.48, -0.305],
    plan: { front: 0.05, rear: 0.04, round: 0.6, tumble: 0.14 },
    doors: 4,
    sculpt: { corner: 0.06, tuck: 0.05, tuckTop: 0.33, swageY: 0.52, swage: 0.012, crown: 0.02, flare: 0.012, screenCrown: 0.05, roofCrown: 0.026 },
  },
  roadster: {
    // Vesper Linnet: a small two-seater with its black hood up: long bonnet, the cockpit well back, short tail.
    lower: [
      [-0.5, 0.42],
      [-0.497, 0.55],
      [-0.47, 0.58],
      [-0.42, 0.59],
      [-0.05, 0.59],
      [0.05, 0.58],
      [0.3, 0.55],
      [0.46, 0.5],
      [0.495, 0.44],
      [0.5, 0.36],
    ],
    cabin: [
      [-0.34, 0.585],
      [-0.27, 0.955],
      [-0.07, 0.965],
      [0.04, 0.585],
    ],
    cabinWidth: 0.8,
    roof: [-0.265, -0.075],
    clearance: 0.14,
    wheelR: 0.3,
    wheelStyle: 'wire',
    bonnet: [0.05, 0.455],
    boot: null,
    plan: { front: 0.14, rear: 0.1, round: 0.8, tumble: 0.16 },
    doors: 2,
    sculpt: { corner: 0.09, tuck: 0.06, tuckTop: 0.3, swageY: null, swage: 0, crown: 0.03, flare: 0.02, screenCrown: 0.06, roofCrown: 0.03 },
  },
};

/**
 * The newer bodies have their own silhouette (DESIGNS) but borrow an older body's trim, lamps, mirrors and
 * interior (the `spec.body` branches below): the estate and the Grandee the Regent's, the Linnet the Vesper's.
 */
const TRIM_OF: Partial<Record<VehicleBodyStyle, VehicleBodyStyle>> = { estate: 'saloon', exec: 'saloon', roadster: 'coupe' };

const cache = new Map<string, VehicleParts>();
// The Blender kit parts (the police lamp) arrive after the first bodies were built: later builds get them.
onKitParts(() => cache.clear());

export function getVehicleParts(spec: VehicleSpec): VehicleParts {
  let parts = cache.get(spec.id);
  if (!parts) {
    const trim = TRIM_OF[spec.body];
    parts =
      spec.body === 'lorry' ? buildLorry(spec) : buildCar(trim ? { ...spec, body: trim } : spec, DESIGNS[spec.body as Exclude<VehicleBodyStyle, 'lorry'>]);
    cache.set(spec.id, parts);
  }
  return parts;
}

/** Shared per-build context for the car builders. */
interface Ctx {
  spec: VehicleSpec;
  d: Design;
  L: number;
  W: number;
  H: number;
  R: number;
  axleF: number;
  axleR: number;
  arches: Arch[];
  /** Body top line in metres (design line, no crown). */
  top: Pt[];
  cab: Pt[];
  beltY: number;
  roofY: number;
  cabW: number;
  bumperY: number;
  /** Front/rear face height (top of the nose / tail). */
  noseY: number;
  tailY: number;
  zF: number;
  zR: number;
  kit: Kit;
  glass: Kit;
  far: Kit;
  police: boolean;
  plan: PlanShape;
  /** The HIGH shell: parts sit on its surface. */
  shell: Shell;
  section: SectionStyle;
  wheelW: number;
  bonnetHinge?: [number, number];
}

function sectionOf(d: Design, H: number): SectionStyle {
  const s = d.sculpt;
  return { corner: s.corner, tuck: s.tuck, tuckTop: s.tuckTop * H, swageY: s.swageY === null ? null : s.swageY * H, swage: s.swage, crown: s.crown, flare: s.flare };
}

function buildCar(spec: VehicleSpec, d: Design): VehicleParts {
  const L = spec.length;
  const W = spec.width;
  const H = spec.height;
  const R = d.wheelR;
  const axleF = spec.wheelbase / 2;
  const axleR = -spec.wheelbase / 2;
  const archR = R + 0.065;
  const arches: Arch[] = [
    { z: axleF, r: archR, cy: R + 0.02 },
    { z: axleR, r: archR, cy: R + 0.02 },
  ];
  const lowerM: Pt[] = d.lower.map(([z, y]) => [z * L, y * H]);
  const cab: Pt[] = d.cabin.map(([z, y]) => [z * L, y * H]);
  const roofY = Math.max(...cab.map((c) => c[1]));
  const beltY = Math.min(cab[0]![1], cab[cab.length - 1]![1]);
  const plan: PlanShape = { halfL: L / 2 + 0.06, ...d.plan, beltY, roofY };
  const wheelW = spec.body === 'van' ? 0.27 : spec.body === 'hatch' ? 0.2 : 0.23;
  const section = sectionOf(d, H);
  const pinch = pinchFn(plan);
  const loft = (detail: 0 | 1 | 2, top: Pt[] = lowerM, halfW = W / 2, arch: Arch[] = arches) =>
    loftShell({ top, clearance: d.clearance, halfW, pinch, arches: arch, wellX: W / 2 - wheelW - 0.1, section, detail });
  const shell = loft(2);
  const c: Ctx = {
    spec,
    d,
    L,
    W,
    H,
    R,
    axleF,
    axleR,
    arches,
    top: lowerM,
    cab,
    beltY,
    roofY,
    cabW: W * d.cabinWidth,
    bumperY: d.clearance + 0.17,
    noseY: lowerM[lowerM.length - 1]![1],
    tailY: lowerM[0]![1],
    zF: L / 2 + 0.045,
    zR: -L / 2 - 0.045,
    kit: new Kit(),
    glass: new Kit(),
    far: new Kit(),
    police: spec.class === 'police',
    plan,
    shell,
    section,
    wheelW,
  };
  const { kit, far } = c;

  // --- body shell, arches, sills
  if (spec.body === 'cab') buildCabShell(c, loft);
  else {
    kit.add(shell.paint, PAINT, 'full').add(shell.dark, DARK, 'full');
    const mid = loft(1);
    kit.add(mid.paint, PAINT, 'mid').add(mid.dark, DARK, 'mid');
    const lo = loft(0);
    far.add(lo.paint, PAINT).add(lo.dark, DARK);
  }
  // Sill: a dark rubber/black strip tucked under the paint between the arches.
  const sillZ0 = axleR + R + 0.14;
  const sillZ1 = axleF - R - 0.14;
  kit.both((sx) => beltBead(c.shell, sx, d.clearance + 0.045, sillZ0, sillZ1, -0.012, 0.016, 0.03, 6), BLACK);

  // --- greenhouse: glass, pillars, roof
  buildGreenhouse(c);

  // --- shut lines, bonnet, boot
  buildPanels(c);

  // --- bumpers, plates, exhaust
  const bumper = buildBumpers(c);

  // --- lamps and grille per model
  const exhaust = buildFace(c);

  // --- mirrors, interior, driver, racks, livery
  kit.fine = true;
  buildMirrors(c);
  kit.label = 'cabin';
  buildInterior(c);
  kit.label = null;
  kit.fine = false;
  kit.label = 'roof';
  buildRack(c);
  if (spec.body === 'cab') buildSign(c, 0xffb43c, (d.roof![1] - 0.07) * L, 0.48);
  kit.label = null;
  buildLivery(c);
  let beacon: THREE.Vector3 | null = null;
  if (c.police) beacon = buildPoliceRoof(c);

  // far LOD: roof + dark glass + lamp blocks
  far.add(extrudeSide(cab, c.cabW, false), { col: 0x10161c, rough: 0.2, metal: 0.4 });
  far.add(box(W * 0.5, 0.12, 0.05, 0, c.bumperY + 0.15, c.zF), HEAD_FAR);
  far.add(box(W * 0.6, 0.1, 0.05, 0, c.bumperY + 0.12, c.zR), TAIL);

  const { full: body, mid } = kit.buildLod(plan);
  const glassLod = c.glass.buildLod(plan);
  const farGeo = far.build(plan);

  const archX = c.shell.flankX(axleF, R + 0.02);
  const wx = archX - wheelW / 2 + 0.012;
  const wheels: WheelPlacement[] = [
    { pos: new THREE.Vector3(wx, R, axleF), radius: R, width: wheelW, steer: true, side: 1 },
    { pos: new THREE.Vector3(-wx, R, axleF), radius: R, width: wheelW, steer: true, side: -1 },
    { pos: new THREE.Vector3(wx, R, axleR), radius: R, width: wheelW, steer: false, side: 1 },
    { pos: new THREE.Vector3(-wx, R, axleR), radius: R, width: wheelW, steer: false, side: -1 },
  ];
  const bonnetHinge: [number, number] | null = c.bonnetHinge ?? (d.bonnet ? [d.bonnet[0] * L + 0.03, shell.surfaceY(d.bonnet[0] * L + 0.03, 0) + 0.02] : null);
  const bootHinge: [number, number] | null = d.boot ? [d.boot[1] * L - 0.03, shell.surfaceY(d.boot[1] * L - 0.03, 0) + 0.02] : null;
  return {
    body,
    mid,
    far: farGeo,
    glass: glassLod.full,
    glassMid: glassLod.mid,
    lights: buildLights(spec, c.bumperY + 0.18),
    wheelStyle: d.wheelStyle,
    wheels,
    bonnetHinge,
    bootHinge,
    bumperF: bumper.front,
    bumperR: bumper.rear,
    beacon,
    exhaust,
    wheelRadius: R,
    engineHeight: H * 0.6,
    arch: [axleF, axleR, R + 0.02, archR],
    clearance: d.clearance,
    triangles: body.getAttribute('position').count / 3,
    trianglesMid: mid.getAttribute('position').count / 3,
    trianglesFar: farGeo.getAttribute('position').count / 3,
    beltY: c.beltY,
    roofY: c.roofY,
    screens: [cab[0]![0], cab[cab.length - 1]![0]],
    extras: kit.labelled,
  };
}

/** Hackney: lofted rear tub + narrow lofted bonnet block + separate flowing front wings over the wheels. */
function buildCabShell(c: Ctx, loft: (detail: 0 | 1 | 2, top?: Pt[], halfW?: number, arch?: Arch[]) => Shell): void {
  const { kit, far, L, W, H, d } = c;
  const clr = d.clearance;
  const zCab = 0.21 * L;
  const rearArch = c.arches[1]!;
  const frontArch = c.arches[0]!;
  // Rear tub up to the windscreen (its own loft: rear arch only).
  const tub: Pt[] = [
    [-0.5 * L, 0.36 * H],
    [-0.495 * L, 0.5 * H],
    [-0.46 * L, 0.53 * H],
    [zCab, 0.53 * H],
  ];
  const tubHi = loft(2, tub, W / 2, [rearArch]);
  kit.add(tubHi.paint, PAINT, 'full').add(tubHi.dark, DARK, 'full');
  const tubMid = loft(1, tub, W / 2, [rearArch]);
  kit.add(tubMid.paint, PAINT, 'mid').add(tubMid.dark, DARK, 'mid');
  const tubLo = loft(0, tub, W / 2, [rearArch]);
  far.add(tubLo.paint, PAINT).add(tubLo.dark, DARK);
  // Bonnet block between the wings, with the tall grille at its nose.
  const bw = W * 0.6;
  const block: Pt[] = [
    [zCab - 0.1, 0.5 * H],
    [0.42 * L, 0.48 * H],
    [0.485 * L, 0.42 * H],
    [0.495 * L, clr + 0.12],
  ];
  const blockSection: SectionStyle = { corner: 0.05, tuck: 0, tuckTop: 0, swageY: null, swage: 0, crown: 0.02, flare: 0 };
  const blockLoft = (detail: 0 | 1 | 2) =>
    loftShell({ top: block.slice(0, 3), clearance: clr + 0.12, halfW: bw / 2, pinch: () => 1, arches: [], wellX: bw / 2 - 0.1, section: blockSection, detail });
  const bHi = blockLoft(2);
  kit.add(bHi.paint, PAINT, 'full').add(bHi.dark, DARK, 'full');
  const bMid = blockLoft(1);
  kit.add(bMid.paint, PAINT, 'mid').add(bMid.dark, DARK, 'mid');
  const bLo = blockLoft(0);
  far.add(bLo.paint, PAINT);
  // Wings: rise just behind the front wheel, sweep over it and down to the bumper. The foot stops
  // short of the door (a wing that ran back along the sill read as a flat slab stuck on the tub).
  const wing: Pt[] = [
    [0.08 * L, clr],
    [0.08 * L, clr + 0.1],
    [0.13 * L, 0.34 * H],
    [0.21 * L, 0.41 * H],
    [0.28 * L, 0.43 * H],
    [0.4 * L, 0.42 * H],
    [0.47 * L, 0.37 * H],
    [0.5 * L, 0.27 * H],
    [0.5 * L, clr + 0.05],
    [frontArch.z + frontArch.r, clr + 0.05],
  ];
  for (let k = 1; k < 12; k++) {
    const t = (k / 12) * Math.PI;
    wing.push([frontArch.z + Math.cos(t) * frontArch.r, frontArch.cy + Math.sin(t) * frontArch.r]);
  }
  wing.push([frontArch.z - frontArch.r, clr]);
  const ww = (W - bw) / 2 + 0.06;
  for (const sx of [1, -1]) {
    kit.add(extrudeSide(densify(wing, 0.2), ww, true, sx * (W / 2 - ww / 2)), PAINT);
    far.add(extrudeSide(wing, ww, false, sx * (W / 2 - ww / 2)), PAINT);
  }
  kit.add(archLiner(frontArch, W - 0.1), DARK);
  kit.both((sx) => archLip(frontArch, sx * (W / 2 - 0.005), 0.024), PAINT);
  // Running boards between the wings and the tub: rubber, which the wear shader leaves matt black.
  kit.both((sx) => box(0.16, 0.04, 0.72, sx * (W / 2 - 0.03), clr + 0.06, -0.05 * L), RUBBER);
  // Bonnet panel and its shut-line slab on top of the block.
  const z0 = zCab - 0.06;
  const z1 = 0.47 * L;
  kit.add(panelLoft(bHi, z0 - 0.02, z1 + 0.02, -bw / 2 + 0.06, bw / 2 - 0.06, 0.001, 0.006, 3, 2), SEAM);
  kit.add(panelLoft(bHi, z0, z1, -bw / 2 + 0.08, bw / 2 - 0.08, 0.006, 0.022, 6, 4), { ...PAINT, ch: CH.BONNET });
  // Centre bonnet hinge line.
  kit.add(box(0.012, 0.012, z1 - z0, 0, bHi.surfaceY((z0 + z1) / 2, 0) + 0.028, (z0 + z1) / 2), { ...CHROME, ch: CH.BONNET });
  c.bonnetHinge = [z0, bHi.surfaceY(z0, 0) + 0.02];
  c.noseY = 0.42 * H;
  c.shell = tubHi;
}

function buildGreenhouse(c: Ctx): void {
  const { kit, glass, cab, cabW, L, W, d, spec } = c;
  const van = spec.body === 'van';
  const sc = d.sculpt;
  // Curved glass: a sweep across the cabin outline. HIGH and MID versions; panes tagged by normal.
  const hi = sweepCabin(cab, cabW / 2, sc.screenCrown, sc.roofCrown, 2, d.roof !== null);
  const mid = sweepCabin(cab, cabW / 2, sc.screenCrown, sc.roofCrown, 1, d.roof !== null);
  glass.add(hi.glass, {}, 'full');
  tagPanes(glass.parts[glass.parts.length - 1]!);
  glass.add(mid.glass, {}, 'mid');
  tagPanes(glass.parts[glass.parts.length - 1]!);
  mid.roof?.dispose();
  if (van) {
    // Cab door windows and the little rear-door windows on the van box.
    for (const sx of [1, -1] as const) {
      const p = box(0.02, 0.32 * c.H, 0.18 * L, sx * (W / 2 + 0.012), 0.77 * c.H, 0.12 * L);
      glass.add(p, { ch: sx > 0 ? 2 : 3 });
      kit.add(box(0.03, 0.34 * c.H + 0.05, 0.03, sx * (W / 2 + 0.01), 0.77 * c.H, 0.12 * L - 0.09 * L - 0.02), BLACK);
      // Rubber window surround.
      kit.add(box(0.012, 0.012, 0.18 * L, sx * (W / 2 + 0.014), 0.77 * c.H + 0.16 * c.H, 0.12 * L), SEAL);
      kit.add(box(0.012, 0.012, 0.18 * L, sx * (W / 2 + 0.014), 0.77 * c.H - 0.16 * c.H, 0.12 * L), SEAL);
    }
    for (const sx of [1, -1] as const) glass.add(box(W * 0.3, 0.26 * c.H, 0.02, sx * W * 0.21, 0.78 * c.H, c.zR - 0.005), { ch: 1 });
    // Screen rubber: a seal across the base and top of the windscreen.
    const [, , fr, fb] = cab as [Pt, Pt, Pt, Pt];
    kit.add(box(cabW + 0.02, 0.025, 0.03, 0, fb[1] + 0.01, fb[0] + 0.01), SEAL);
    kit.add(box(cabW + 0.02, 0.025, 0.03, 0, fr[1] - 0.005, fr[0] + 0.02), SEAL);
    return;
  }
  // Pillars (painted): A at the windscreen, C at the rear screen, B mid-cabin on four-doors.
  const [rb, rr, fr, fb] = cab as [Pt, Pt, Pt, Pt];
  const px = cabW / 2 - 0.03;
  const aw = spec.body === 'cab' ? 0.07 : 0.075;
  const cw = spec.body === 'saloon' ? 0.22 : spec.body === 'coupe' ? 0.16 : 0.09;
  for (const sx of [1, -1] as const) {
    kit.add(edgeBeam(sx * px, fb[0] - 0.01, fb[1] - 0.01, fr[0], fr[1] + 0.005, aw, 0.07, -1), PAINT);
    const ct = Math.min(cw, 0.12);
    kit.add(edgeBeam(sx * (cabW / 2 - ct / 2 + 0.008), rb[0] + 0.02, rb[1] - 0.01, rr[0] + 0.02, rr[1] + 0.005, ct, cw, 1), PAINT);
    if (d.doors === 4) {
      const zb = fb[0] + (rb[0] - fb[0]) * 0.52;
      const yt = lineY([rr, fr], zb);
      kit.add(box(0.03, yt - c.beltY + 0.02, 0.09, sx * (cabW / 2 + 0.004), (yt + c.beltY) / 2, zb), PAINT);
    } else if (spec.body === 'hatch') {
      // Quarter-light divider.
      const zq = rb[0] + (fb[0] - rb[0]) * 0.33;
      const yt = lineY([rr, fr], Math.max(rr[0], zq));
      kit.add(box(0.025, yt - c.beltY, 0.035, sx * (cabW / 2 + 0.004), (yt + c.beltY) / 2, zq), PAINT);
    } else if (spec.body === 'coupe') {
      // Opening quarter-light at the front of the door glass.
      const zq = fb[0] - 0.42;
      const yt = lineY([rr, fr], zq);
      kit.add(box(0.02, yt - c.beltY - 0.02, 0.02, sx * (cabW / 2 + 0.004), (yt + c.beltY) / 2, zq), CHROME);
    }
    // Bright window surround along the belt and up the door glass edges; black on the cab.
    const trim = spec.body === 'cab' ? BLACK : CHROME;
    kit.add(box(0.02, 0.018, Math.abs(fb[0] - rb[0]) - 0.1, sx * (cabW / 2 + 0.006), c.beltY + 0.012, (fb[0] + rb[0]) / 2), trim);
    // Rubber seals where the screens meet the body.
    kit.add(box(0.014, 0.02, 0.02, sx * (cabW / 2 - 0.1), fb[1] + 0.005, fb[0] + 0.01), SEAL);
  }
  // Screen seals across the base of both screens (the panels under them are painted deck).
  kit.add(box(cabW - 0.12, 0.022, 0.03, 0, fb[1] + 0.008, fb[0] + 0.012), SEAL);
  kit.add(box(cabW - 0.12, 0.022, 0.03, 0, rb[1] + 0.008, rb[0] - 0.012), SEAL);
  // Roof: the cambered sweep skin (paint, ROOF channel so it can go vinyl/contrast) with drip rails.
  if (d.roof && hi.roof) {
    kit.add(hi.roof, { ...PAINT, ch: CH.ROOF });
    const z0 = rr[0] + 0.02;
    const z1 = fr[0] - 0.02;
    const hw = cabW / 2;
    kit.both((sx) => box(0.016, 0.02, z1 - z0, sx * (hw + 0.008), c.roofY + 0.004, (z0 + z1) / 2), spec.body === 'hatch' ? PAINT : DULL_CHROME);
    c.far.add(box(cabW, 0.04, z1 - z0 + 0.1, 0, c.roofY + 0.01, (z0 + z1) / 2), { ...PAINT, ch: CH.ROOF });
  } else hi.roof?.dispose();
}

/** Glass panes by face normal: 0 windscreen, 1 rear screen, 2 left, 3 right. */
function tagPanes(g: THREE.BufferGeometry): void {
  const n = g.getAttribute('normal');
  const surf = g.getAttribute('surf') as THREE.BufferAttribute;
  for (let i = 0; i < n.count; i++) {
    const nx = n.getX(i);
    const nz = n.getZ(i);
    const ny = n.getY(i);
    let pane = 0;
    if (Math.abs(nx) > 0.6) pane = nx > 0 ? 2 : 3;
    else if (nz < -0.2) pane = 1;
    else if (nz > 0.2 || ny > 0) pane = 0;
    surf.setW(i, pane);
  }
}

function buildPanels(c: Ctx): void {
  const { kit, d, L, W, spec, shell } = c;
  const seamTop = c.beltY - 0.03;
  const seamBot = d.clearance + 0.1;
  const fb = c.cab[c.cab.length - 1]![0];
  const rb = c.cab[0]![0];
  if (spec.body === 'van') {
    // Cab door, sliding side door on the kerb (left) side, rear doors.
    const vanSeams = [0.24 * L, 0.0, -0.16 * L];
    const vTop = 0.62 * c.H + 0.06;
    kit.both((sx) => flankStrip(shell, sx, vanSeams[0]!, seamBot, vTop, 0.003, 0.004, 0.007), SEAM);
    kit.both((sx) => flankStrip(shell, sx, vanSeams[1]!, seamBot, vTop, 0.003, 0.004, 0.007), SEAM);
    kit.add(flankStrip(shell, 1, vanSeams[2]!, seamBot, 0.85 * c.H, 0.003, 0.004, 0.007), SEAM);
    kit.add(box(0.008, 0.012, 0.16 * L, W / 2 + 0.004, 0.86 * c.H, -0.08 * L), DULL_CHROME); // slide rail
    kit.add(box(0.014, 0.8 * c.H - seamBot, 0.008, 0, (0.8 * c.H + seamBot) / 2 + 0.05, c.zR - 0.003), SEAM);
    kit.both((sx) => box(0.12, 0.03, 0.03, sx * 0.1, 0.55 * c.H, c.zR - 0.01), CHROME);
    // Rubbing strips.
    kit.both((sx) => beltBead(shell, sx, 0.45 * c.H, -0.47 * L, 0.43 * L, 0.008, 0.012, 0.025, 10), BLACK);
    // Door handles.
    kit.both((sx) => box(0.025, 0.025, 0.1, sx * (shell.flankX(vanSeams[0]! + 0.1, seamTop - 0.3) + 0.01), seamTop - 0.3, vanSeams[0]! + 0.1), CHROME);
  } else {
    const seams = d.doors === 4 ? [fb - 0.02, fb + (rb - fb) * 0.52, rb + 0.06] : [fb - 0.02, rb + (fb - rb) * 0.33 - 0.04];
    for (const z of seams) kit.both((sx) => flankStrip(shell, sx, z, seamBot, seamTop, 0.002, 0.004, 0.007), SEAM);
    const z0 = Math.min(seams[0]!, seams[seams.length - 1]!);
    const z1 = Math.max(seams[0]!, seams[seams.length - 1]!);
    kit.both((sx) => beltBead(shell, sx, seamBot, z0, z1, 0.002, 0.004, 0.006, 6), SEAM);
    // Door handles (chrome push-buttons) just under the belt.
    const handleZ = d.doors === 4 ? [seams[1]! + 0.12, seams[2]! + 0.12] : [seams[1]! + 0.14];
    for (const z of handleZ) kit.both((sx) => box(0.03, 0.028, 0.12, sx * (shell.flankX(z, seamTop - 0.06) + 0.012), seamTop - 0.06, z), CHROME);
    // Chrome waist strip that follows the flank (not on the cab, which is all business).
    if (spec.body !== 'cab') {
      const wy = spec.body === 'coupe' ? c.beltY - 0.1 : c.beltY - 0.12;
      kit.both((sx) => beltBead(shell, sx, wy, -L * 0.42, L * 0.4, 0.006, 0.006, 0.012, 12), CHROME);
    }
  }
  // Bonnet: a crowned panel 2 cm proud of the body, with a dark slab beneath that reads as the shut
  // line and becomes the engine bay when the bonnet pops.
  if (d.bonnet) {
    const z0 = d.bonnet[0] * L;
    const z1 = d.bonnet[1] * L;
    const bw = W - 0.2;
    kit.add(panelLoft(shell, z0 - 0.015, z1 + 0.015, -bw / 2 - 0.015, bw / 2 + 0.015, 0.001, 0.006, 4, 4), SEAM);
    kit.add(panelLoft(shell, z0, z1, -bw / 2, bw / 2, 0.006, 0.024, 8, 6), { ...PAINT, ch: CH.BONNET }, 'full');
    kit.add(panelLoft(shell, z0, z1, -bw / 2, bw / 2, 0.006, 0.024, 3, 2), { ...PAINT, ch: CH.BONNET }, 'mid');
    if (spec.body === 'coupe') {
      // Power bulge and louvres.
      kit.add(panelLoft(shell, z0 + 0.25, z1 - 0.35, -bw * 0.17, bw * 0.17, 0.02, 0.07, 6, 4), { ...PAINT, ch: CH.BONNET });
      for (let i = 0; i < 6; i++) {
        const z = z0 + 0.35 + i * 0.09;
        kit.both((sx) => box(0.26, 0.012, 0.025, sx * bw * 0.32, shell.surfaceY(z, bw * 0.32) + 0.03, z), { col: 0x0a0a0a, rough: 0.8, ch: CH.BONNET });
      }
    }
    if (spec.body === 'saloon') {
      // Centre bonnet crease.
      kit.add(box(0.03, 0.006, z1 - z0 - 0.1, 0, shell.surfaceY((z0 + z1) / 2, 0) + 0.027, (z0 + z1) / 2), { ...PAINT, ch: CH.BONNET });
    }
  }
  if (d.boot) {
    const z0 = d.boot[0] * L;
    const z1 = d.boot[1] * L;
    const bw = W - 0.24;
    kit.add(panelLoft(shell, z0 - 0.015, z1 + 0.015, -bw / 2 - 0.015, bw / 2 + 0.015, 0.001, 0.006, 3, 4), SEAM);
    kit.add(panelLoft(shell, z0, z1, -bw / 2, bw / 2, 0.006, 0.022, 6, 6), { ...PAINT, ch: CH.BOOT }, 'full');
    kit.add(panelLoft(shell, z0, z1, -bw / 2, bw / 2, 0.006, 0.022, 2, 2), { ...PAINT, ch: CH.BOOT }, 'mid');
  }
}

function buildBumpers(c: Ctx): { front: [number, number, number]; rear: [number, number, number] } {
  const { kit, W, spec } = c;
  const y = c.bumperY;
  const zf = c.zF + 0.04;
  const zr = c.zR - 0.04;
  const hw = W / 2 + 0.03;
  const chF = { ...CHROME, ch: CH.BUMPER_F };
  const chR = { ...CHROME, ch: CH.BUMPER_R };
  const rubberF = { ...RUBBER, ch: CH.BUMPER_F };
  const rubberR = { ...RUBBER, ch: CH.BUMPER_R };
  if (spec.body === 'coupe') {
    // Slim split blades either side of the mouth, plus a full rear blade.
    kit.both((sx) => box(W * 0.3, 0.06, 0.07, sx * W * 0.33, y + 0.04, zf - 0.02), chF);
    kit.both((sx) => box(0.07, 0.06, 0.22, sx * (hw - 0.04), y + 0.04, zf - 0.12), chF);
    kit.both((sx) => box(W * 0.28, 0.012, 0.075, sx * W * 0.33, y + 0.04, zf - 0.02), rubberF); // rubber insert
  } else {
    const h = spec.body === 'van' ? 0.16 : 0.12;
    kit.add(box(W + 0.04, h, 0.1, 0, y, zf), chF);
    kit.add(box(W - 0.3, 0.016, 0.104, 0, y, zf), rubberF); // rubber strip along the blade
    kit.both((sx) => box(0.08, h, 0.3, sx * (hw - 0.03), y, zf - 0.16), chF);
    if (spec.body !== 'van') {
      kit.both((sx) => box(0.07, 0.24, 0.06, sx * W * 0.24, y + 0.04, zf + 0.05), chF); // overriders
      kit.both((sx) => box(0.074, 0.05, 0.062, sx * W * 0.24, y + 0.04, zf + 0.05), rubberF); // overrider rubber face
    }
  }
  kit.add(box(W + 0.04, 0.12, 0.1, 0, y, zr), chR);
  kit.add(box(W - 0.3, 0.016, 0.104, 0, y, zr), rubberR);
  kit.both((sx) => box(0.08, 0.12, 0.3, sx * (hw - 0.03), y, zr + 0.16), chR);
  if (spec.body === 'saloon' || spec.body === 'cab') {
    kit.both((sx) => box(0.07, 0.22, 0.06, sx * W * 0.24, y + 0.03, zr - 0.05), chR);
    kit.both((sx) => box(0.074, 0.05, 0.062, sx * W * 0.24, y + 0.03, zr - 0.05), rubberR);
  }
  // Number plates: black backing + glyph face. Front sits under the grille, rear above the bumper.
  // The front plate rides on the bumper (and hangs with it); the coupe's sits in its mouth.
  const fy = spec.body === 'coupe' ? y + 0.02 : y;
  const fz = spec.body === 'coupe' ? zf + 0.03 : zf + 0.058;
  const fch = spec.body === 'coupe' ? CH.STATIC : CH.BUMPER_F;
  kit.label = 'plateF';
  kit.add(box(0.44, 0.1, 0.012, 0, fy, fz - 0.006), { ...BLACK, ch: fch });
  kit.add(plateQuad(0.42, 0.085, 0, fy, fz + 0.001, 1), { ...PLATE, ch: fch });
  const ry = y + 0.15;
  kit.label = 'plateR';
  kit.add(box(0.44, 0.1, 0.012, 0, ry, c.zR - 0.008), BLACK);
  kit.add(plateQuad(0.42, 0.085, 0, ry, c.zR - 0.016, -1), PLATE);
  kit.add(box(0.1, 0.02, 0.03, 0, ry + 0.065, c.zR - 0.02), CHROME); // plate lamp hood
  kit.label = null;
  c.far.add(box(W + 0.04, 0.12, 0.1, 0, y, zf), CHROME);
  c.far.add(box(W + 0.04, 0.12, 0.1, 0, y, zr), CHROME);
  return { front: [zf, y, hw], rear: [zr, y, hw] };
}

/** Grille, lamps and tail details per model. Returns exhaust outlets. */
function buildFace(c: Ctx): THREE.Vector3[] {
  const { kit, W, H, spec, zF, zR, bumperY, shell } = c;
  const by = bumperY;
  const ex: THREE.Vector3[] = [new THREE.Vector3(-W * 0.3, c.d.clearance + 0.07, zR - 0.12)];
  const pipe = (x: number) => {
    kit.add(discZ(0.035, 0.18, x, c.d.clearance + 0.07, zR + 0.06, -1, 8), DULL_CHROME);
    kit.add(discZ(0.026, 0.02, x, c.d.clearance + 0.07, zR - 0.11, -1, 8), DARK);
  };
  /** Rectangular lens with a chrome surround, on both flanks (`s` may depend on the side). */
  const lamps = (w: number, h: number, x: number, y: number, z: number, facing: 1 | -1, s: Surf | ((sx: 1 | -1) => Surf)) => {
    for (const sx of [1, -1] as const) {
      kit.add(box(w, h, 0.04, sx * x, y, z), typeof s === 'function' ? s(sx) : s);
      kit.add(box(w + 0.016, h + 0.016, 0.012, sx * x, y, z + facing * 0.006), DULL_CHROME);
    }
  };
  switch (spec.body) {
    case 'hatch': {
      // Wide "moustache" grille, big round lamps in the wing fronts, sidelights below.
      const gy = by + 0.2;
      kit.add(box(W * 0.56, 0.17, 0.03, 0, gy, zF - 0.005), DARK);
      for (let i = 0; i < 4; i++) kit.add(box(W * 0.56, 0.014, 0.03, 0, gy - 0.06 + i * 0.04, zF + 0.005), CHROME);
      kit.add(box(W * 0.58, 0.02, 0.035, 0, gy + 0.09, zF + 0.004), CHROME);
      const lx = W / 2 - 0.22;
      const ly = by + 0.27;
      for (const sx of [1, -1]) headlamp(kit, 0.1, sx * lx, ly, zF - 0.035, 1, 14);
      kit.both((sx) => box(0.08, 0.05, 0.04, sx * lx, by + 0.12, zF - 0.005), (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
      // Tall slim tail lamps: red over amber, reverse lamp under the plate.
      lamps(0.08, 0.13, W / 2 - 0.12, by + 0.3, zR + 0.005, -1, TAIL);
      lamps(0.08, 0.06, W / 2 - 0.12, by + 0.2, zR + 0.005, -1, (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
      kit.add(box(0.08, 0.04, 0.03, -0.3, by + 0.07, zR - 0.02), REV);
      // External welded seams: the Minnow's signature lips at the A-pillar base and round the roof.
      kit.both((sx) => beam(sx * (shell.flankX(c.cab[3]![0], c.beltY - 0.1) + 0.004), c.cab[3]![0] + 0.03, c.d.clearance + 0.1, c.cab[3]![0] + 0.01, c.beltY + 0.03, 0.02, 0.025), PAINT);
      kit.add(box(0.05, 0.015, 0.03, 0, c.noseY + 0.02, zF - 0.01), CHROME); // badge
      pipe(-W * 0.3);
      break;
    }
    case 'saloon': {
      // Full-width chrome grille with vertical bars between twin round lamps each side.
      const gy = by + 0.22;
      const gw = W * 0.5;
      kit.add(box(gw, 0.2, 0.03, 0, gy, zF - 0.005), DARK);
      for (let i = 0; i <= 10; i++) kit.add(box(0.012, 0.2, 0.03, -gw / 2 + (gw * i) / 10, gy, zF + 0.005), CHROME);
      kit.add(box(gw + 0.04, 0.025, 0.04, 0, gy + 0.11, zF + 0.006), CHROME);
      kit.add(box(gw + 0.04, 0.025, 0.04, 0, gy - 0.11, zF + 0.006), CHROME);
      for (const off of [0, 0.2]) {
        const lx = W / 2 - 0.15 - off;
        for (const sx of [1, -1]) headlamp(kit, 0.075, sx * lx, gy + 0.02, zF - 0.035, 1, 12);
      }
      lamps(0.14, 0.04, W / 2 - 0.25, by + 0.1, zF - 0.01, 1, (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
      kit.add(box(0.12, 0.03, 0.02, 0, c.noseY + 0.03, zF - 0.02), CHROME); // badge
      kit.add(box(0.016, 0.07, 0.06, 0, c.noseY + 0.05, zF - 0.1), CHROME); // bonnet mascot
      // Wraparound horizontal tail clusters: red, amber, reverse.
      const ty = by + 0.22;
      lamps(0.34, 0.1, W / 2 - 0.22, ty, zR + 0.012, -1, TAIL);
      kit.both((sx) => box(0.05, 0.1, 0.2, sx * (shell.flankX(zR + 0.1, ty) + 0.01), ty, zR + 0.1), TAIL);
      lamps(0.12, 0.1, W / 2 - 0.45, ty, zR + 0.012, -1, (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
      lamps(0.08, 0.1, W / 2 - 0.56, ty, zR + 0.012, -1, REV);
      kit.add(box(W * 0.6, 0.02, 0.04, 0, ty + 0.08, zR + 0.01), CHROME); // boot finisher
      // Hint of tail fins along the rear wings.
      kit.both((sx) => beam(sx * (W / 2 - 0.05), zR + 0.04, c.tailY + 0.07, zR + 0.75, c.tailY + 0.05, 0.08, 0.05), PAINT);
      pipe(-W * 0.3);
      break;
    }
    case 'coupe': {
      // Oval mouth with a chrome surround and centre bar, cowled lamps under clear covers.
      const my = by + 0.08;
      const mouth = sphere(1, 0, my, zF - 0.06, W * 0.2, 0.12, 0.06, 12);
      kit.add(mouth, DARK);
      kit.add(ringZ(1, 0.1, 0, 0, 0, 16).scale(W * 0.2, 0.12, 0.3).translate(0, my, zF - 0.01), CHROME);
      kit.add(box(W * 0.36, 0.014, 0.03, 0, my, zF - 0.01), CHROME);
      // Cowled lamps: a painted nacelle rising out of the sloping nose with a chrome-rimmed fluted lens
      // in its face (an opaque "clear cover" read as a white ball; the lens must be the lamp).
      const lx = W / 2 - 0.24;
      const ly = c.noseY + 0.03;
      kit.both((sx) => sphere(0.1, sx * lx, ly, zF - 0.13, 1, 0.85, 1.3, 12), PAINT, 'both');
      // Lens face a touch proud of the nacelle tip so the paint never shows through the glass.
      for (const sx of [1, -1]) headlamp(kit, 0.068, sx * lx, ly, zF - 0.02, 1, 12);
      kit.both((sx) => box(0.1, 0.035, 0.05, sx * (W / 2 - 0.12), by + 0.02, zF - 0.06), (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
      // Twin round tail lamps per side.
      const ty = by + 0.2;
      kit.both((sx) => discZ(0.06, 0.04, sx * (W / 2 - 0.16), ty, zR + 0.02, -1, 12), TAIL);
      kit.both((sx) => discZ(0.05, 0.04, sx * (W / 2 - 0.33), ty, zR + 0.02, -1, 12), (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
      kit.both((sx) => ringZ(0.065, 0.01, sx * (W / 2 - 0.16), ty, zR - 0.02, 12), CHROME);
      kit.both((sx) => ringZ(0.055, 0.009, sx * (W / 2 - 0.33), ty, zR - 0.02, 12), CHROME);
      kit.add(box(0.07, 0.04, 0.03, 0.3, by + 0.08, zR - 0.01), REV);
      kit.add(box(W * 0.5, 0.016, 0.03, 0, ty + 0.1, zR - 0.005), CHROME); // tail finisher
      // Side vents behind the front wheels, filler cap on the rear wing.
      for (let i = 0; i < 3; i++) {
        const vy = c.d.clearance + 0.22 + i * 0.05;
        kit.both((sx) => box(0.012, 0.018, 0.22, sx * (shell.flankX(c.axleF - c.R - 0.25, vy) + 0.006), vy, c.axleF - c.R - 0.25), CHROME);
      }
      kit.add(cylX(0.05, 0.03, shell.flankX(c.axleR - 0.15, c.beltY - 0.06) + 0.01, c.beltY - 0.06, c.axleR - 0.15, 10), CHROME);
      // Twin exhausts.
      pipe(-W * 0.26);
      pipe(-W * 0.34);
      ex.push(new THREE.Vector3(-W * 0.34, c.d.clearance + 0.07, zR - 0.12));
      break;
    }
    case 'cab': {
      // Tall upright grille on the bonnet block; round lamps on top of the wings.
      const gw = W * 0.34;
      const gy = by + 0.28;
      kit.add(box(gw, 0.36, 0.03, 0, gy, zF - 0.01), DARK);
      for (let i = 0; i <= 8; i++) kit.add(box(0.014, 0.36, 0.03, -gw / 2 + (gw * i) / 8, gy, zF), CHROME);
      kit.add(box(gw + 0.05, 0.03, 0.04, 0, gy + 0.19, zF), CHROME);
      kit.add(box(gw + 0.05, 0.03, 0.04, 0, gy - 0.19, zF), CHROME);
      kit.both((sx) => box(0.014, 0.36, 0.04, sx * (gw / 2 + 0.018), gy, zF), CHROME);
      kit.add(box(0.1, 0.06, 0.04, 0, gy + 0.24, zF - 0.02), CHROME); // mascot plinth
      const lx = W / 2 - 0.17;
      const ly = 0.43 * H - 0.03;
      kit.both((sx) => discZ(0.12, 0.1, sx * lx, ly + 0.02, 0.47 * c.L - 0.13, 1, 12), PAINT, 'both'); // lamp pod
      for (const sx of [1, -1]) headlamp(kit, 0.095, sx * lx, ly + 0.02, 0.47 * c.L - 0.03, 1, 14);
      kit.both((sx) => box(0.07, 0.05, 0.04, sx * (W / 2 - 0.17), by + 0.12, zF - 0.02), (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
      const ty = by + 0.25;
      kit.both((sx) => discZ(0.07, 0.04, sx * (W / 2 - 0.15), ty, zR + 0.02, -1, 12), TAIL);
      kit.both((sx) => ringZ(0.074, 0.01, sx * (W / 2 - 0.15), ty, zR - 0.02, 12), CHROME);
      kit.both((sx) => discZ(0.05, 0.04, sx * (W / 2 - 0.15), ty - 0.15, zR + 0.02, -1, 12), (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
      kit.add(box(0.07, 0.04, 0.03, 0.25, by + 0.08, zR - 0.01), REV);
      pipe(-W * 0.3);
      break;
    }
    case 'van': {
      // Chrome-framed rectangular grille with the round lamps inside it.
      const gy = by + 0.3;
      const gw = W * 0.82;
      kit.add(box(gw, 0.3, 0.03, 0, gy, zF - 0.01), DARK);
      for (let i = 0; i < 5; i++) kit.add(box(gw * 0.5, 0.014, 0.03, 0, gy - 0.1 + i * 0.05, zF), CHROME);
      kit.add(box(gw + 0.04, 0.025, 0.04, 0, gy + 0.16, zF), CHROME);
      kit.add(box(gw + 0.04, 0.025, 0.04, 0, gy - 0.16, zF), CHROME);
      kit.both((sx) => box(0.025, 0.34, 0.04, sx * (gw / 2 + 0.01), gy, zF), CHROME);
      const lx = gw / 2 - 0.14;
      for (const sx of [1, -1]) headlamp(kit, 0.1, sx * lx, gy, zF - 0.01, 1, 14);
      kit.both((sx) => box(0.08, 0.06, 0.04, sx * (W / 2 - 0.08), gy - 0.02, zF - 0.02), (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
      // Tail lamps high on the corner posts.
      const ty = by + 0.35;
      lamps(0.1, 0.18, W / 2 - 0.08, ty, zR + 0.005, -1, TAIL);
      lamps(0.1, 0.07, W / 2 - 0.08, ty - 0.14, zR + 0.005, -1, (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
      kit.add(box(0.08, 0.05, 0.03, 0.4, by + 0.1, zR - 0.01), REV);
      // Roof gutters along the box.
      kit.both((sx) => box(0.025, 0.03, c.L * 0.72, sx * (W / 2 + 0.005), 0.975 * H + 0.03, -0.13 * c.L), DULL_CHROME);
      pipe(-W * 0.3);
      break;
    }
  }
  return ex;
}

function buildMirrors(c: Ctx): void {
  const { kit, W, spec, cab, shell } = c;
  const fb = cab[cab.length - 1]!;
  if (spec.body === 'van') {
    // Black arm mirrors on the cab doors.
    kit.both((sx) => box(0.2, 0.025, 0.025, sx * (W / 2 + 0.1), 0.66 * c.H, 0.24 * c.L - 0.06), BLACK);
    kit.both((sx) => box(0.04, 0.18, 0.11, sx * (W / 2 + 0.2), 0.7 * c.H, 0.24 * c.L - 0.06), BLACK);
    kit.both((sx) => box(0.01, 0.15, 0.09, sx * (W / 2 + 0.2) - sx * 0.022, 0.7 * c.H, 0.24 * c.L - 0.06), { col: 0xc8ccd0, rough: 0.05, metal: 0.9 });
    return;
  }
  const MIRROR: Surf = { col: 0x8fa0a8, rough: 0.04, metal: 0.95 };
  if (spec.body === 'coupe') {
    kit.both((sx) => cylY(0.014, 0.012, 0.06, sx * (c.cabW / 2 + 0.06), c.beltY, fb[0] - 0.12, 6), CHROME);
    kit.both((sx) => sphere(0.055, sx * (c.cabW / 2 + 0.08), c.beltY + 0.085, fb[0] - 0.13, 1, 0.8, 1.2, 8), CHROME);
    kit.both((sx) => discZ(0.04, 0.012, sx * (c.cabW / 2 + 0.08), c.beltY + 0.085, fb[0] - 0.13 - 0.04, -1, 10), MIRROR);
    return;
  }
  // Chrome bullet mirrors out on the front wings, very much of the period: a short stout stalk and a
  // proper head with a glass face (a thin pin with a pea on top read as an aerial).
  const z = spec.body === 'cab' ? 0.28 * c.L : fb[0] + 0.55;
  const x = W / 2 - 0.1;
  const y = spec.body === 'cab' ? 0.43 * c.H + 0.04 : shell.surfaceY(z, x);
  kit.both((sx) => cylY(0.016, 0.012, 0.07, sx * x, y, z, 6), CHROME);
  kit.both((sx) => sphere(0.058, sx * x, y + 0.1, z - 0.015, 1, 0.8, 1.15, 8), CHROME);
  kit.both((sx) => discZ(0.042, 0.012, sx * x, y + 0.1, z - 0.015 - 0.04, -1, 10), MIRROR);
}

function buildInterior(c: Ctx): void {
  const { kit, W, cab, spec, beltY, shell } = c;
  const van = spec.body === 'van';
  const fb = cab[cab.length - 1]![0];
  const rb = cab[0]![0];
  const front = van ? 0.23 * c.L : fb;
  const rear = van ? 0.0 : rb;
  const len = front - rear;
  const floor = c.d.clearance + 0.12;
  const iw = (van ? W : c.cabW) - 0.14;
  /** Inside height under the roof/glass at z (the van's box is tall everywhere). */
  const ceiling = (z: number) => (van ? 0.95 * c.H : lineY(cab, z)) - 0.09;
  kit.add(box(iw, 0.04, Math.max(0.3, len), 0, floor, (front + rear) / 2), DARK);
  if (!van) {
    // The body shell is solid up to the belt, so what shows through the glass is its top face:
    // cover it with dark carpet/trim, or the cabin reads as bare paint (an empty, bright tub).
    let deck = 0;
    for (let k = 0; k <= 8; k++) deck = Math.max(deck, shell.surfaceY(rear + (len * k) / 8, 0));
    kit.add(box(c.cabW - 0.06, 0.012, Math.max(0.3, len - 0.04), 0, deck + 0.008, (front + rear) / 2), { col: 0x1d1915, rough: 0.95 });
    // Rear parcel shelf and door cards.
    if (len > 1.6 && spec.body !== 'coupe') kit.add(box(c.cabW - 0.1, 0.02, 0.3, 0, beltY - 0.02, rear + 0.18), UPHOL);
  }
  // Dashboard: walnut in the saloon, black elsewhere; a binnacle and a rear-view mirror.
  const dashCol: Surf = spec.body === 'saloon' && !c.police ? { col: 0x4a2e18, rough: 0.35, metal: 0.05 } : { col: 0x161616, rough: 0.7 };
  kit.add(box(iw, 0.12, 0.2, 0, beltY - 0.03, front - 0.12), dashCol);
  kit.add(box(iw - 0.1, 0.03, 0.12, 0, beltY + 0.035, front - 0.14), { col: 0x111111, rough: 0.8 });
  const frTop = cab[cab.length - 2]!;
  kit.add(box(0.2, 0.05, 0.015, 0, frTop[1] - 0.1, frTop[0] - 0.12), { col: 0x202224, rough: 0.3, metal: 0.4 });
  // Seats: buckets in front (a bench in the van), a bench behind where there is room for one.
  const seatZ = front - Math.min(0.78, len * 0.45);
  const seat = (x: number, w: number, z: number) => {
    const top = Math.min(beltY + 0.32, ceiling(z - 0.3) - 0.05);
    kit.add(box(w, 0.12, 0.46, x, floor + 0.24, z), UPHOL);
    kit.add(beam(x, z - 0.22, floor + 0.26, z - 0.3, top, w, 0.1), UPHOL);
    kit.add(box(w - 0.06, 0.02, 0.1, x, top + 0.02, z - 0.3), UPHOL); // headrest roll
  };
  const dx = W * 0.22;
  if (van) seat(0, iw - 0.1, seatZ);
  else {
    seat(dx, 0.46, seatZ);
    seat(-dx, 0.46, seatZ);
    if (len > 1.6 && spec.body !== 'coupe') seat(0, iw - 0.08, rear + 0.45);
  }
  if (spec.body === 'cab') kit.add(box(iw, Math.max(0.2, beltY + 0.35 - floor - 0.3), 0.03, 0, (beltY + 0.35 + floor + 0.3) / 2, seatZ - 0.42), { col: 0x9fb0b8, rough: 0.1, metal: 0.3 }); // partition
  // Steering wheel (right-hand drive: the driver sits on the -X side).
  const wz = front - 0.32;
  const wy = beltY + 0.03;
  const wheel = new THREE.TorusGeometry(0.17, 0.018, 4, 12);
  wheel.rotateX(-0.45);
  wheel.translate(-dx, wy, wz);
  kit.add(wheel, { col: 0x111111, rough: 0.6 });
  kit.add(beam(-dx, wz, wy, wz + 0.16, wy - 0.18, 0.04, 0.04), { col: 0x111111, rough: 0.6 });
  // Driver, scaled to sit under this car's roof; shown only while someone is at the wheel.
  const hipY = floor + 0.3;
  const sz = seatZ;
  const s = Math.min(1, Math.max(0.6, (ceiling(sz - 0.16) - hipY) / 0.8));
  const torso = 0.48 * s;
  const hr = 0.1 * Math.min(1, 0.25 + s * 0.8);
  const headY = hipY + torso + 0.05 + hr;
  kit.add(beam(-dx, sz - 0.16, hipY, sz - 0.2, hipY + torso, 0.36, 0.22), COAT);
  kit.add(box(0.34, 0.14, 0.42, -dx, hipY - 0.03, sz + 0.04), COAT);
  kit.add(box(0.07, 0.06, 0.08, -dx, hipY + torso + 0.03, sz - 0.18), SKIN);
  kit.add(sphere(hr, -dx, headY, sz - 0.17, 1, 1.12, 1.05, 8), SKIN);
  for (const sx of [1, -1]) kit.add(beam(-dx + sx * 0.16, sz - 0.15, hipY + torso * 0.85, wz - 0.06, wy - 0.01, 0.075, 0.075), COAT);
  if (c.police) {
    kit.add(cylY(hr * 1.15, hr * 1.25, 0.05, -dx, headY + hr * 0.55, sz - 0.17, 10), { col: 0x10131d, rough: 0.7, ch: CH.DRIVER });
    kit.add(box(hr * 1.9, 0.012, hr * 1.1, -dx, headY + hr * 0.6, sz - 0.08), { col: 0x050505, rough: 0.3, ch: CH.DRIVER });
  } else if (spec.body === 'cab' || spec.body === 'van') {
    kit.add(sphere(hr * 1.15, -dx, headY + hr * 0.55, sz - 0.15, 1, 0.38, 1.2, 8), { col: 0x3a352c, rough: 0.9, ch: CH.DRIVER }); // flat cap
  } else {
    kit.add(sphere(hr * 1.03, -dx, headY + hr * 0.25, sz - 0.19, 1, 0.78, 1.02, 8), HAIR);
  }
}

/**
 * The roof rack (Minnow, Regent) or ladder rack (Haulmaster), on every body that can carry one; the shader collapses
 * it on a car without (CH.RACK, uShow.y). The Blender racks from the wheels pack (wheels_gen.py k_rack_*, built to
 * these bodies' roof spans; packGeometry.ts kitPart) when it is in, with their origin on the rails' centre line over
 * the roof's centre, else the procedural tube-and-box rack.
 */
function buildRack(c: Ctx): void {
  const { kit, cabW, spec, L, W } = c;
  const rack = { col: 0xb4b2ab, rough: 0.3, metal: 0.9, ch: CH.RACK };
  const id = spec.body === 'van' ? 'rack-ladder' : spec.body === 'hatch' ? 'rack-hatch' : spec.body === 'saloon' ? 'rack-saloon' : null;
  const baked = id ? kitPart(id, 'high') : null;
  const bakedMid = id && baked ? kitPart(id, 'mid') : null;
  const place = (y: number, z: number): boolean => {
    if (!baked || !bakedMid) {
      baked?.dispose();
      return false;
    }
    kit.addBaked(baked.translate(0, y, z), 'full');
    kit.addBaked(bakedMid.translate(0, y, z), 'mid');
    return true;
  };
  if (spec.body === 'van') {
    // Ladder rack with an aluminium ladder lashed on.
    const y = 0.975 * c.H + 0.12;
    const z0 = -0.46 * L;
    const z1 = 0.22 * L;
    if (place(y, (z0 + z1) / 2)) return;
    kit.both((sx) => box(0.04, 0.04, z1 - z0, sx * (W / 2 - 0.12), y, (z0 + z1) / 2), { ...rack, col: 0x2a2a2a, rough: 0.6, metal: 0.3 });
    for (let i = 0; i <= 4; i++) kit.add(box(W - 0.2, 0.04, 0.04, 0, y, z0 + ((z1 - z0) * i) / 4), { ...rack, col: 0x2a2a2a, rough: 0.6, metal: 0.3 });
    kit.both((sx) => box(0.05, 0.05, (z1 - z0) * 0.95, sx * 0.22, y + 0.05, (z0 + z1) / 2), rack);
    for (let i = 0; i < 12; i++) kit.add(box(0.44, 0.025, 0.025, 0, y + 0.06, z0 + 0.15 + i * ((z1 - z0 - 0.3) / 11)), rack);
    return;
  }
  if (!c.d.roof) {
    baked?.dispose();
    bakedMid?.dispose();
    return;
  }
  const z0 = c.d.roof[0] * L + 0.05;
  const z1 = c.d.roof[1] * L - 0.05;
  const y = c.roofY + 0.1;
  if (place(y, (z0 + z1) / 2)) return;
  kit.both((sx) => box(0.025, 0.025, z1 - z0, sx * (cabW / 2 - 0.04), y, (z0 + z1) / 2), rack);
  for (let i = 0; i < 4; i++) {
    const z = z0 + ((z1 - z0) * i) / 3;
    kit.add(box(cabW - 0.06, 0.02, 0.025, 0, y, z), rack);
    kit.both((sx) => box(0.02, 0.09, 0.02, sx * (cabW / 2 - 0.04), y - 0.05, z), rack);
  }
  // A strapped-down suitcase on the Minnow rack.
  if (spec.body === 'hatch') kit.add(box(0.55, 0.16, 0.42, 0.1, y + 0.09, (z0 + z1) / 2), { col: 0x5a3a24, rough: 0.8, ch: CH.RACK });
}

/** Optional secondary paint: coupe stripes, van trade band. Collapsed when a car has none. */
function buildLivery(c: Ctx): void {
  const { kit, spec, L, W, d, shell } = c;
  if (spec.body === 'coupe') {
    const s = { ...PAINT2 };
    for (const sx of [1, -1]) {
      const x = sx * 0.13;
      if (d.bonnet) {
        // Over the bonnet, riding up and over the power bulge (z0+0.25 .. z1-0.35).
        const z0 = d.bonnet[0] * L;
        const z1 = d.bonnet[1] * L;
        const sb = { ...s, ch: CH.BONNET };
        kit.add(panelLoft(shell, z0 + 0.02, z0 + 0.25, x - 0.065, x + 0.065, 0.0245, 0.027, 2, 2), sb);
        kit.add(panelLoft(shell, z0 + 0.25, z1 - 0.35, x - 0.065, x + 0.065, 0.0705, 0.073, 5, 2), sb);
        kit.add(panelLoft(shell, z1 - 0.35, z1 - 0.02, x - 0.065, x + 0.065, 0.0245, 0.027, 2, 2), sb);
      }
      kit.add(panelLoft(shell, d.bonnet![1] * L, L / 2 - 0.04, x - 0.065, x + 0.065, 0.0005, 0.004, 3, 2), s);
      kit.add(panelLoft(shell, -L / 2 + 0.06, c.cab[0]![0], x - 0.065, x + 0.065, 0.0005, 0.004, 3, 2), s);
      if (d.roof) kit.add(box(0.13, 0.004, (d.roof[1] - d.roof[0]) * L + 0.12, x, c.roofY + 0.045, ((d.roof[0] + d.roof[1]) / 2) * L), s);
      // Up the fastback glass edge isn't painted; the stripe resumes on the tail.
    }
  } else if (spec.body === 'van') {
    kit.both((sx) => beltBead(shell, sx, 0.56 * c.H, -0.46 * L, 0.46 * L, 0.001, 0.004, 0.1, 10), PAINT2);
    kit.add(box(W * 0.8, 0.2, 0.01, 0, 0.56 * c.H, c.zR - 0.004), PAINT2);
  }
}

/**
 * Police: blue waist band, POLICE roof sign and the single rotating blue lamp. The lamp is the Blender one from the
 * wheels pack when it is in (art/models/blender/wheels_gen.py `beacon`: a fluted dome on a plinth; high in the
 * detailed body, mid in the mid one), else the procedural dome.
 */
function buildPoliceRoof(c: Ctx): THREE.Vector3 {
  const { kit, W, L, d, shell } = c;
  const blue: Surf = { col: 0x1d3f8f, rough: 0.45, metal: 0.1 };
  kit.both((sx) => beltBead(shell, sx, c.beltY - 0.2, -L * 0.45, L * 0.45, 0.002, 0.005, 0.065, 12), blue);
  kit.add(box(W * 0.7, 0.13, 0.012, 0, c.beltY - 0.2, c.zR - 0.006), blue);
  kit.label = 'police';
  // A body without a roof span (the van): the lamp and the sign sit on the cab roof.
  const roof = d.roof ?? [d.cabin[1]![0], d.cabin[2]![0]];
  const rz = ((roof[0] + roof[1]) / 2) * L;
  const y = c.roofY + 0.03;
  const baked = kitPart('beacon', 'high');
  const bakedMid = baked ? kitPart('beacon', 'mid') : null;
  if (baked && bakedMid) {
    kit.addBaked(baked.translate(0, y, rz - 0.15), 'full');
    kit.addBaked(bakedMid.translate(0, y, rz - 0.15), 'mid');
  } else {
    baked?.dispose();
    kit.add(cylY(0.09, 0.08, 0.05, 0, y, rz - 0.15, 12), { col: 0x202020, rough: 0.5 });
    const dome = new THREE.SphereGeometry(0.085, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    dome.scale(1, 1.6, 1);
    dome.translate(0, y + 0.05, rz - 0.15);
    kit.add(dome, lens(0x2048d8, CH.BEACON));
    kit.add(cylY(0.085, 0.085, 0.05, 0, y + 0.05, rz - 0.15, 12), lens(0x2048d8, CH.BEACON));
  }
  buildSign(c, 0xf2eee0, rz + 0.12, 0.62);
  kit.label = null;
  return new THREE.Vector3(0, y + 0.1, rz - 0.15);
}

/** Illuminated roof sign box (POLICE / TAXI): glyph faces front and back. */
function buildSign(c: Ctx, col: number, z: number, w: number): void {
  const y = c.roofY + 0.1;
  const h = 0.13;
  const s: Surf = { ...lens(col, CH.SIGN), glyph: 2 };
  const top: Surf = lens(col, CH.SIGN);
  c.kit.add(box(w, h, 0.12, 0, y, z), { col: 0x1b1b1b, rough: 0.5 });
  c.kit.add(plateQuad(w - 0.03, h - 0.025, 0, y, z + 0.061, 1), s);
  c.kit.add(plateQuad(w - 0.03, h - 0.025, 0, y, z - 0.061, -1), s);
  c.kit.add(box(w - 0.03, 0.01, 0.1, 0, y + h / 2 + 0.003, z), top);
  c.kit.add(box(0.06, 0.06, 0.06, 0, y - h / 2 - 0.02, z), { col: 0x1b1b1b, rough: 0.5 });
}

// ------------------------------------------------------------------------------------ lorry

function buildLorry(spec: VehicleSpec): VehicleParts {
  const L = spec.length;
  const W = spec.width;
  const H = spec.height;
  const R = 0.52;
  const cabLen = 2.3;
  const cabZ0 = L / 2 - cabLen;
  const axleF = L / 2 - 1.15;
  const axleR1 = axleF - spec.wheelbase;
  const axleR2 = axleR1 - 1.2;
  const clearance = 0.45;
  const kit = new Kit();
  const glass = new Kit();
  const far = new Kit();
  const fA: Arch = { z: axleF, r: R + 0.09, cy: R + 0.02 };

  // Forward-control cab: upright, slightly raked split screen, wrap-round corners.
  const cabTop: Pt[] = [
    [cabZ0, H * 0.98],
    [L / 2 - 0.4, H],
    [L / 2 - 0.08, H * 0.64],
    [L / 2, H * 0.3],
  ];
  const plan: PlanShape = { halfL: L / 2 + 0.2, front: 0.04, rear: 0, round: 0.5, beltY: H * 0.62, roofY: H, tumble: 0 };
  const section: SectionStyle = { corner: 0.08, tuck: 0.02, tuckTop: H * 0.3, swageY: null, swage: 0, crown: 0.025, flare: 0.015 };
  const loft = (detail: 0 | 1 | 2) => loftShell({ top: cabTop, clearance, halfW: W / 2, pinch: pinchFn(plan), arches: [fA], wellX: W / 2 - 0.5, section, detail });
  const shell = loft(2);
  kit.add(shell.paint, PAINT, 'full').add(shell.dark, DARK, 'full');
  const mid = loft(1);
  kit.add(mid.paint, PAINT, 'mid').add(mid.dark, DARK, 'mid');
  const lo = loft(0);
  far.add(lo.paint, PAINT).add(lo.dark, DARK);
  const gl: Pt[] = [
    [L / 2 - 0.62, H * 0.62],
    [L / 2 - 0.62, H * 0.93],
    [L / 2 - 0.33, H * 0.95],
    [L / 2 - 0.05, H * 0.62],
  ];
  const sweep = sweepCabin(gl, W * 0.485, 0.04, 0, 2, false);
  glass.add(sweep.glass, {}, 'full');
  tagPanes(glass.parts[glass.parts.length - 1]!);
  const sweepMid = sweepCabin(gl, W * 0.485, 0.04, 0, 1, false);
  glass.add(sweepMid.glass, {}, 'mid');
  tagPanes(glass.parts[glass.parts.length - 1]!);
  kit.add(beam(0, L / 2 - 0.05, H * 0.62, L / 2 - 0.32, H * 0.95, 0.06, 0.05), PAINT); // screen divider
  kit.add(box(W * 0.97, 0.025, 0.03, 0, H * 0.62 + 0.01, L / 2 - 0.04), SEAL);
  far.add(extrudeSide(gl, W * 0.97, false), { col: 0x10161c, rough: 0.2, metal: 0.4 });
  // Cab door seam + handle, step.
  kit.both((sx) => flankStrip(shell, sx, cabZ0 + 0.85, H * 0.35, H * 0.9, 0.003, 0.004, 0.007), SEAM);
  kit.both((sx) => box(0.03, 0.03, 0.14, sx * (shell.flankX(cabZ0 + 1.0, H * 0.6) + 0.012), H * 0.6, cabZ0 + 1.0), CHROME);
  kit.both((sx) => box(0.25, 0.04, 0.35, sx * (W / 2 - 0.1), clearance + 0.05, axleF - R - 0.35), DULL_CHROME);
  // Grille, bumper, lamps, marker lights, roof.
  kit.add(box(W * 0.62, 0.42, 0.04, 0, H * 0.42, L / 2 + 0.02), DARK);
  for (let i = 0; i < 6; i++) kit.add(box(W * 0.62, 0.02, 0.04, 0, H * 0.42 - 0.18 + i * 0.07, L / 2 + 0.035), CHROME);
  const bumper = box(W + 0.1, 0.24, 0.2, 0, clearance + 0.12, L / 2 + 0.08);
  kit.add(bumper, { ...BLACK, ch: CH.BUMPER_F });
  far.add(box(W + 0.1, 0.24, 0.2, 0, clearance + 0.12, L / 2 + 0.08), BLACK);
  const lampX = W / 2 - 0.3;
  for (const sx of [1, -1]) headlamp(kit, 0.14, sx * lampX, 0.95, L / 2 + 0.03, 1, 14);
  kit.both((sx) => box(0.1, 0.07, 0.04, sx * (W / 2 - 0.08), 0.95, L / 2 + 0.02), (sx) => IND(sx > 0 ? CH.IND_FL : CH.IND_FR));
  for (let i = 0; i < 3; i++) kit.add(box(0.1, 0.05, 0.06, -0.35 + i * 0.35, H + 0.04, L / 2 - 0.45), lens(0xe08a1a, CH.STATIC));
  kit.label = 'plateF';
  kit.add(box(0.44, 0.1, 0.012, 0, clearance + 0.12, L / 2 + 0.185), BLACK);
  kit.add(plateQuad(0.42, 0.085, 0, clearance + 0.12, L / 2 + 0.192, 1), PLATE);
  kit.label = null;
  // Mirrors on arms.
  kit.both((sx) => box(0.3, 0.025, 0.025, sx * (W / 2 + 0.15), H * 0.72, L / 2 - 0.55), BLACK);
  kit.both((sx) => box(0.05, 0.28, 0.14, sx * (W / 2 + 0.3), H * 0.75, L / 2 - 0.55), BLACK);
  far.add(box(W * 0.5, 0.15, 0.05, 0, 0.95, L / 2 + 0.03), HEAD_FAR);

  // Chassis, fuel tank, rear mudguards, ram.
  kit.both((sx) => box(0.12, 0.25, L * 0.96, sx * 0.45, clearance + 0.1, -0.1), BLACK);
  kit.add(discZ(0.22, 0.9, -(W / 2 - 0.32), clearance + 0.12, cabZ0 - 0.95, 1, 12), DULL_CHROME); // fuel tank
  for (const z of [axleR1, axleR2]) {
    kit.both((sx) => box(0.4, 0.04, 1.2, sx * (W / 2 - 0.18), R * 2 + 0.12, z), BLACK);
    kit.both((sx) => box(0.4, 0.3, 0.04, sx * (W / 2 - 0.18), R * 2 - 0.03, z - 0.6), RUBBER);
  }
  kit.add(cylY(0.09, 0.09, 0.42, 0, 0.55, cabZ0 - 0.4, 10), DULL_CHROME); // tipper ram, stops under the bed floor
  // Tipper bed (secondary paint): floor, ribbed walls, tailgate, headboard over the cab.
  const bedL = cabZ0 - -L / 2 - 0.15;
  const bedZ = -L / 2 + bedL / 2;
  const bedY = 1.05;
  const wallH = 1.0;
  const bed: Surf = { ...PAINT2 };
  kit.add(box(W, 0.12, bedL, 0, bedY, bedZ), bed);
  kit.both((sx) => box(0.08, wallH, bedL, sx * (W / 2 - 0.04), bedY + wallH / 2, bedZ), bed);
  for (let i = 0; i <= 6; i++) kit.both((sx) => box(0.05, wallH, 0.07, sx * (W / 2 + 0.01), bedY + wallH / 2, -L / 2 + 0.1 + (i * (bedL - 0.2)) / 6), bed);
  kit.both((sx) => box(0.06, 0.06, bedL, sx * (W / 2 + 0.01), bedY + wallH, bedZ), bed);
  kit.add(box(W, wallH, 0.08, 0, bedY + wallH / 2, -L / 2 + 0.04), bed);
  kit.add(box(W - 0.2, 0.05, 0.05, 0, bedY + wallH * 0.75, -L / 2 - 0.01), BLACK);
  kit.add(box(W, wallH + 0.35, 0.1, 0, bedY + (wallH + 0.35) / 2, cabZ0 - 0.15), bed);
  kit.add(box(W, 0.06, 0.4, 0, bedY + wallH + 0.35, cabZ0 - 0.0), bed);
  // Dust and scuffs inside the bed.
  kit.add(box(W - 0.2, 0.01, bedL - 0.2, 0, bedY + 0.065, bedZ), { col: 0x5a5146, rough: 1 });
  far.add(box(W, wallH, bedL, 0, bedY + wallH / 2, bedZ), PAINT2);
  // Tail lamps, rear plate, rear bumper.
  const tY = 0.8;
  kit.both((sx) => box(0.26, 0.14, 0.05, sx * (W / 2 - 0.25), tY, -L / 2 - 0.03), TAIL);
  kit.both((sx) => box(0.1, 0.1, 0.05, sx * (W / 2 - 0.47), tY, -L / 2 - 0.03), (sx) => IND(sx > 0 ? CH.IND_RL : CH.IND_RR));
  kit.add(box(0.08, 0.06, 0.04, 0, tY, -L / 2 - 0.03), REV);
  kit.add(box(W - 0.3, 0.12, 0.12, 0, 0.62, -L / 2 - 0.05), { ...BLACK, ch: CH.BUMPER_R });
  kit.label = 'plateR';
  kit.add(box(0.44, 0.1, 0.012, 0, tY + 0.02, -L / 2 - 0.035), BLACK);
  kit.add(plateQuad(0.42, 0.085, 0, tY + 0.02, -L / 2 - 0.042, -1), PLATE);
  kit.label = null;
  far.add(box(W * 0.8, 0.12, 0.05, 0, tY, -L / 2 - 0.03), TAIL);
  // Cab interior + driver (sits high, right-hand drive).
  const floorY = H * 0.5;
  const dx = W * 0.25;
  const sz = L / 2 - 1.15;
  kit.fine = true;
  kit.label = 'cabin';
  kit.add(box(W - 0.2, 0.15, 0.3, 0, H * 0.6, L / 2 - 0.25), { col: 0x161616, rough: 0.7 });
  kit.add(box(W - 0.3, 0.12, 0.5, 0, floorY + 0.2, sz), UPHOL);
  kit.add(beam(0, sz - 0.27, floorY + 0.2, sz - 0.32, floorY + 0.75, W - 0.3, 0.12), UPHOL);
  const wheel = new THREE.TorusGeometry(0.22, 0.02, 4, 12);
  wheel.rotateX(-1.1);
  wheel.translate(-dx, H * 0.66, L / 2 - 0.45);
  kit.add(wheel, { col: 0x111111, rough: 0.6 });
  const hipY = floorY + 0.3;
  kit.add(beam(-dx, sz - 0.12, hipY, sz - 0.16, hipY + 0.5, 0.4, 0.24), COAT);
  kit.add(sphere(0.11, -dx, hipY + 0.66, sz - 0.12, 1, 1.1, 1.05, 8), SKIN);
  kit.add(sphere(0.125, -dx, hipY + 0.72, sz - 0.1, 1, 0.38, 1.2, 8), { col: 0x3a352c, rough: 0.9, ch: CH.DRIVER });
  for (const sx of [1, -1]) kit.add(beam(-dx + sx * 0.18, sz - 0.1, hipY + 0.42, L / 2 - 0.5, H * 0.66, 0.08, 0.08), COAT);
  kit.fine = false;
  kit.label = null;

  const { full: body, mid: midGeo } = kit.buildLod(plan);
  const glassLod = glass.buildLod(plan);
  const farGeo = far.build();
  const wheelW = 0.36;
  const wx = W / 2 - wheelW / 2 + 0.02;
  const wheels: WheelPlacement[] = [
    { pos: new THREE.Vector3(wx, R, axleF), radius: R, width: wheelW, steer: true, side: 1 },
    { pos: new THREE.Vector3(-wx, R, axleF), radius: R, width: wheelW, steer: true, side: -1 },
  ];
  for (const z of [axleR1, axleR2]) {
    wheels.push({ pos: new THREE.Vector3(wx, R, z), radius: R, width: wheelW * 1.25, steer: false, side: 1 });
    wheels.push({ pos: new THREE.Vector3(-wx, R, z), radius: R, width: wheelW * 1.25, steer: false, side: -1 });
  }
  return {
    body,
    mid: midGeo,
    far: farGeo,
    glass: glassLod.full,
    glassMid: glassLod.mid,
    lights: buildLights(spec, 0.95),
    wheelStyle: 'truck',
    wheels,
    bonnetHinge: null,
    bootHinge: null,
    bumperF: [L / 2 + 0.08, clearance + 0.12, W / 2 + 0.05],
    bumperR: [-L / 2 - 0.05, 0.62, W / 2 - 0.15],
    beacon: null,
    exhaust: [new THREE.Vector3(W / 2 - 0.2, 0.5, cabZ0 - 0.9)],
    wheelRadius: R,
    engineHeight: H * 0.6,
    arch: [axleF, axleR1, R, R + 0.09],
    clearance,
    triangles: body.getAttribute('position').count / 3,
    trianglesMid: midGeo.getAttribute('position').count / 3,
    trianglesFar: farGeo.getAttribute('position').count / 3,
    beltY: plan.beltY,
    roofY: H,
    screens: [cabZ0, L / 2 - 0.3],
    extras: kit.labelled,
  };
}

// ------------------------------------------------------------------------------------ light pools

/**
 * Additive light geometry for one model: a soft pool on the road ahead (lch 0), two volumetric
 * beams from the lamps (lch 1, HIGH quality only), a red glow behind (lch 2) and a white reversing
 * pool (lch 3). Per-vertex colour carries the falloff, so no texture is needed.
 */
function buildLights(spec: VehicleSpec, lampY: number): THREE.BufferGeometry {
  const L = spec.length;
  const W = spec.width;
  const pos: number[] = [];
  const col: number[] = [];
  const lch: number[] = [];
  const tri = (a: number[], b: number[], c: number[], ca: number[], cb: number[], cc: number[], ch: number) => {
    pos.push(...a, ...b, ...c);
    col.push(...ca, ...cb, ...cc);
    lch.push(ch, ch, ch);
  };
  /** Elliptical fan on the road: bright centre, black rim. */
  const pool = (cx: number, cz: number, rx: number, rz: number, colour: number[], ch: number, seg = 14) => {
    const zero = [0, 0, 0];
    for (let i = 0; i < seg; i++) {
      const a0 = (i / seg) * Math.PI * 2;
      const a1 = ((i + 1) / seg) * Math.PI * 2;
      tri([cx, 0.045, cz], [cx + Math.cos(a1) * rx, 0.045, cz + Math.sin(a1) * rz], [cx + Math.cos(a0) * rx, 0.045, cz + Math.sin(a0) * rz], colour, zero, zero, ch);
    }
  };
  // Tungsten-warm but not orange; a shorter throw than a modern lamp (soft, 1970 sealed beams). Warmer and a
  // little tighter than the first pass: on a phone at dusk the pale pools read as grey fog patches ahead of
  // every car rather than light on the tarmac (docs/ART-DIRECTION.md).
  const warm = [1.0, 0.85, 0.6];
  pool(0, L / 2 + 3.6, W * 0.95, 3.8, warm.map((v) => v * 0.25), 0);
  pool(0, L / 2 + 1.4, W * 0.7, 1.6, warm.map((v) => v * 0.14), 0);
  // Volumetric beams: a flattened cone from each lamp, fading to nothing ~6 m out.
  const lx = W / 2 - 0.3;
  for (const sx of [1, -1]) {
    const x0 = sx * lx;
    const z0 = L / 2 + 0.1;
    const z1 = z0 + 5.5;
    const spread = 0.85;
    const ring = 6;
    const bright = warm.map((v) => v * 0.07);
    const zero = [0, 0, 0];
    for (let i = 0; i < ring; i++) {
      const a0 = (i / ring) * Math.PI * 2;
      const a1 = ((i + 1) / ring) * Math.PI * 2;
      const p0 = [x0 + sx * 0.6 + Math.cos(a0) * spread, Math.max(0.05, lampY - 0.4 + Math.sin(a0) * spread * 0.4), z1];
      const p1 = [x0 + sx * 0.6 + Math.cos(a1) * spread, Math.max(0.05, lampY - 0.4 + Math.sin(a1) * spread * 0.4), z1];
      tri([x0, lampY, z0], p0, p1, bright, zero, zero, 1);
    }
  }
  const red = [1.0, 0.12, 0.06];
  pool(0, -L / 2 - 0.5, W * 0.5, 0.75, red.map((v) => v * 0.17), 2);
  pool(0, -L / 2 - 1.6, W * 0.6, 1.9, [0.32, 0.32, 0.3], 3);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('lch', new THREE.Float32BufferAttribute(lch, 1));
  g.computeBoundingSphere();
  return g;
}

// ------------------------------------------------------------------------------------ wheels

const wheelCache = new Map<string, THREE.BufferGeometry>();

const TYRE: Surf = { col: 0x141414, rough: 0.9 };
const TYRE_BACK: Surf = { col: 0x0d0d0d, rough: 0.9 };

/** A lathe around the axle (profile points (r, x)) into the kit, the hub face toward +X. */
function latheInto(kit: Kit, pts: Pt[], s: Surf, seg: number): void {
  const lg = new THREE.LatheGeometry(
    pts.map(([r, y]) => new THREE.Vector2(r, y)),
    seg,
  );
  lg.rotateZ(-Math.PI / 2);
  kit.add(lg, s);
}

/**
 * The tyre of the unit wheel, from the bead at `rim`: rounded shoulders, two circumferential tread grooves and
 * a raised sidewall band (full), or the 12-sided plain one (lite), plus the dark disc that closes it from behind.
 */
function addTyre(kit: Kit, rim: number, lite: boolean): void {
  if (lite) {
    latheInto(
      kit,
      [
        [rim, -0.44],
        [0.8, -0.5],
        [0.95, -0.45],
        [1.0, -0.36],
        [1.0, 0.36],
        [0.95, 0.45],
        [0.8, 0.5],
        [rim, 0.44],
      ],
      TYRE,
      12,
    );
    latheInto(kit, [[0, -0.4], [rim, -0.42]], TYRE_BACK, 8);
    return;
  }
  // Tyre: inner sidewall → tread (grooved) → outer sidewall with a raised band, down to the rim.
  latheInto(
    kit,
    [
      [rim, -0.44],
      [0.8, -0.5],
      [0.93, -0.47],
      [0.99, -0.38],
      [1.0, -0.3],
      [0.975, -0.26],
      [0.975, -0.22],
      [1.0, -0.18],
      [1.0, -0.04],
      [0.975, 0.0],
      [0.975, 0.04],
      [1.0, 0.08],
      [1.0, 0.22],
      [0.975, 0.26],
      [0.975, 0.3],
      [0.99, 0.36],
      [0.93, 0.47],
      [0.86, 0.5],
      [0.84, 0.515],
      [0.8, 0.5],
      [rim, 0.44],
    ],
    TYRE,
    16,
  );
  // Inner face disc (dark) so the wheel is closed from behind.
  latheInto(kit, [[0, -0.4], [rim, -0.42]], TYRE_BACK, 10);
}

/** The tyre alone (a Blender rim is merged with it, rimModels.ts). Fresh geometry each call. */
export function tyreGeometry(rim: number, lite: boolean): THREE.BufferGeometry {
  const kit = new Kit();
  addTyre(kit, rim, lite);
  return kit.build();
}

/** Default finish of each Blender rim (rimModels.ts), for the stand-in and the lite dish: the moodboard's bronze, white, gunmetal, silver, matt black. */
const MODERN: Partial<Record<WheelStyle, Surf>> = {
  lattice: { col: 0xb4b6b2, rough: 0.38, metal: 0.65, slot: 1 },
  forged: { col: 0x8c5c30, rough: 0.38, metal: 0.65, slot: 1 },
  ninestar: { col: 0xe8e6dc, rough: 0.38, metal: 0.65, slot: 1 },
  twelve: { col: 0x3d4042, rough: 0.38, metal: 0.65, slot: 1 },
  twinspoke: { col: 0xb8bab6, rough: 0.38, metal: 0.65, slot: 1 },
  honeycomb: { col: 0x1c1c1c, rough: 0.72, metal: 0.35, slot: 1 },
};

/**
 * Unit wheel (radius 1, width 1, axle along X, hub face toward +X) for instancing: a tyre with
 * rounded shoulders, two circumferential tread grooves and a raised sidewall band, then the rim
 * and centre per style. `lite` is the MEDIUM/LOW version (fewer segments, no grooves, nuts, slots
 * or spokes), swapped in for the whole batch by wheelBatch.ts. For a Blender rim (MODERN) this is
 * only the stand-in, a plain dish in its finish: wheelBatch draws the real one once its pack is in.
 */
export function wheelGeometry(style: WheelStyle, lite = false): THREE.BufferGeometry {
  const key = `${style}:${lite ? 'lite' : 'full'}`;
  let g = wheelCache.get(key);
  if (g) return g;
  const kit = new Kit();
  const SEG = lite ? 12 : 16;
  const lathe = (pts: Pt[], s: Surf, seg = SEG) => latheInto(kit, pts, s, seg);
  const rim = MODERN[style] ? 0.7 : style === 'hubcap' ? 0.56 : style === 'wire' ? 0.68 : style === 'truck' ? 0.62 : style === 'disc' ? 0.58 : style === 'minilite' ? 0.66 : style === 'rostyle' ? 0.64 : 0.6;
  addTyre(kit, rim, lite);
  if (lite) {
    // slot 1 = takes a rim colour (Pike's spray bay; the wheel shader's per-instance tint). Not the truck's.
    const dish: Surf =
      style === 'hubcap' ? { col: 0xa8aaa8, rough: 0.45, metal: 0.6, slot: 1 }
      : style === 'chrome' ? { ...CHROME, slot: 1 }
      : style === 'wire' ? { col: 0x4a4b48, rough: 0.5, metal: 0.6, slot: 1 }
      : style === 'steel' ? { col: 0xd8d0b8, rough: 0.5, metal: 0.2, slot: 1 }
      : style === 'disc' ? { col: 0x1c1c1c, rough: 0.55, metal: 0.3, slot: 1 }
      : style === 'minilite' ? { col: 0x9a9c98, rough: 0.4, metal: 0.55, slot: 1 }
      : style === 'rostyle' ? { col: 0x6a6c6a, rough: 0.45, metal: 0.5, slot: 1 }
      : (MODERN[style] ?? { col: 0x7a1e14, rough: 0.55, metal: 0.25 });
    lathe(
      [
        [rim, 0.44],
        [rim - 0.05, 0.42],
        [0.3, style === 'truck' ? 0.3 : 0.42],
        [0.26, 0.44],
      ],
      dish,
    );
    lathe(
      [
        [0.26, 0.44],
        [0.15, 0.5],
        [0, 0.52],
      ],
      style === 'truck' ? DULL_CHROME : CHROME,
    );
    if (style === 'wire') lathe([[rim - 0.02, 0.46], [rim - 0.06, 0.44], [rim - 0.1, 0.4]], CHROME);
    g = kit.build();
    wheelCache.set(key, g);
    return g;
  }
  const nuts = (n: number, r: number, x: number, size: number) => {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.PI / n;
      kit.add(cylX(size, size * 1.2, x, Math.cos(a) * r, Math.sin(a) * r, 6), DULL_CHROME);
    }
  };
  switch (style) {
    case 'hubcap': {
      // Silver-painted steel wheel with ventilation slots and a small chrome cap (Minnow).
      lathe(
        [
          [rim, 0.44],
          [rim - 0.03, 0.46],
          [rim - 0.06, 0.4],
          [0.42, 0.38],
          [0.36, 0.42],
          [0.3, 0.42],
        ],
        { col: 0xa8aaa8, rough: 0.45, metal: 0.6, slot: 1 },
      );
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        kit.add(box(0.02, 0.09, 0.05, 0.4, Math.cos(a) * 0.45, Math.sin(a) * 0.45), { col: 0x0a0a0a, rough: 0.9 });
      }
      lathe(
        [
          [0.3, 0.42],
          [0.27, 0.48],
          [0.15, 0.52],
          [0, 0.53],
        ],
        CHROME,
      );
      break;
    }
    case 'chrome': {
      // Full chrome hubcap with a ribbed ring and a domed centre (Regent).
      lathe(
        [
          [rim, 0.44],
          [rim - 0.02, 0.48],
          [0.52, 0.46],
          [0.44, 0.5],
          [0.3, 0.52],
          [0.18, 0.56],
          [0, 0.57],
        ],
        { ...CHROME, slot: 1 },
      );
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        kit.add(box(0.04, 0.07, 0.02, 0.49, Math.cos(a) * 0.5, Math.sin(a) * 0.5), { col: 0x8a8880, rough: 0.3, metal: 0.9 });
      }
      break;
    }
    case 'wire': {
      // Wire wheel: chrome rim ring, a dark well, a lacing of fine spokes and a two-eared knock-off (Vesper GT).
      lathe(
        [
          [rim, 0.44],
          [rim - 0.02, 0.48],
          [rim - 0.05, 0.42],
          [rim - 0.09, 0.3],
          [0.2, 0.24],
          [0, 0.24],
        ],
        { col: 0x1a1a1c, rough: 0.6, metal: 0.4 },
      );
      lathe(
        [
          [rim, 0.44],
          [rim - 0.02, 0.48],
          [rim - 0.05, 0.42],
          [rim - 0.09, 0.3],
        ],
        CHROME,
      );
      const N = 16;
      for (let i = 0; i < N; i++) {
        const a = (i / N) * Math.PI * 2;
        for (const twist of [0.55, -0.55]) {
          const sp = new THREE.BoxGeometry(0.014, rim - 0.12, 0.014);
          sp.translate(0, (rim - 0.12) / 2 + 0.16, 0);
          sp.rotateZ(twist * 0.25);
          sp.rotateX(a + twist * 0.3);
          sp.translate(0.42, 0, 0);
          kit.add(sp, { col: 0xc9cbc6, rough: 0.25, metal: 0.9, slot: 1 });
        }
      }
      kit.add(cylX(0.17, 0.1, 0.4, 0, 0, 12), { col: 0xb0b2ad, rough: 0.3, metal: 0.85 });
      kit.add(cylX(0.12, 0.1, 0.48, 0, 0, 10), CHROME);
      for (let i = 0; i < 2; i++) {
        const ear = new THREE.BoxGeometry(0.06, 0.4, 0.05);
        ear.rotateX((i * Math.PI) / 2 + Math.PI / 4);
        ear.translate(0.53, 0, 0);
        kit.add(ear, CHROME);
      }
      break;
    }
    case 'steel': {
      // Painted steel wheel (cream) with slots and a small chrome cap (Haulmaster).
      lathe(
        [
          [rim, 0.44],
          [rim - 0.05, 0.42],
          [0.42, 0.38],
          [0.3, 0.42],
          [0.22, 0.43],
        ],
        { col: 0xd8d0b8, rough: 0.5, metal: 0.2, slot: 1 },
      );
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        kit.add(box(0.02, 0.1, 0.06, 0.4, Math.cos(a) * 0.48, Math.sin(a) * 0.48), { col: 0x0a0a0a, rough: 0.9 });
      }
      nuts(5, 0.2, 0.44, 0.03);
      lathe(
        [
          [0.22, 0.43],
          [0.2, 0.48],
          [0.1, 0.5],
          [0, 0.5],
        ],
        CHROME,
      );
      break;
    }
    case 'disc': {
      // Plain black steel disc with a chrome hub cap (Hackney).
      lathe(
        [
          [rim, 0.44],
          [rim - 0.04, 0.42],
          [0.4, 0.4],
          [0.3, 0.44],
          [0.26, 0.44],
        ],
        { col: 0x1c1c1c, rough: 0.55, metal: 0.3, slot: 1 },
      );
      nuts(5, 0.34, 0.45, 0.03);
      lathe(
        [
          [0.26, 0.44],
          [0.22, 0.5],
          [0.1, 0.53],
          [0, 0.54],
        ],
        CHROME,
      );
      break;
    }
    case 'minilite': {
      // Eight-spoke alloy (the rally look): a polished lip, a dark well and eight broad spokes to a nutted hub.
      const ALLOY: Surf = { col: 0x9a9c98, rough: 0.38, metal: 0.6, slot: 1 };
      lathe(
        [
          [rim, 0.44],
          [rim - 0.02, 0.47],
          [rim - 0.06, 0.43],
          [rim - 0.08, 0.36],
        ],
        ALLOY,
      );
      lathe(
        [
          [rim - 0.08, 0.36],
          [0.3, 0.33],
          [0, 0.33],
        ],
        { col: 0x141414, rough: 0.8 },
      );
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const sp = new THREE.BoxGeometry(0.07, rim - 0.26, 0.11);
        sp.translate(0, (rim - 0.26) / 2 + 0.2, 0);
        sp.rotateX(a);
        sp.translate(0.4, 0, 0);
        kit.add(sp, ALLOY);
      }
      lathe(
        [
          [0.22, 0.4],
          [0.2, 0.46],
          [0.1, 0.48],
          [0, 0.48],
        ],
        ALLOY,
      );
      nuts(4, 0.13, 0.48, 0.025);
      break;
    }
    case 'rostyle': {
      // Pressed-steel sports wheel: a dished face with five raised ribs and a small chrome cap.
      const PRESSED: Surf = { col: 0x6a6c6a, rough: 0.45, metal: 0.5, slot: 1 };
      lathe(
        [
          [rim, 0.44],
          [rim - 0.03, 0.46],
          [rim - 0.07, 0.4],
          [0.36, 0.34],
          [0.26, 0.4],
          [0.2, 0.42],
        ],
        PRESSED,
      );
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const rib = new THREE.BoxGeometry(0.06, rim - 0.3, 0.07);
        rib.translate(0, (rim - 0.3) / 2 + 0.22, 0);
        rib.rotateX(a);
        rib.translate(0.4, 0, 0);
        kit.add(rib, { col: 0x8a8c88, rough: 0.35, metal: 0.6, slot: 1 });
      }
      nuts(5, 0.26, 0.42, 0.028);
      lathe(
        [
          [0.2, 0.42],
          [0.17, 0.47],
          [0.08, 0.49],
          [0, 0.49],
        ],
        CHROME,
      );
      break;
    }
    case 'truck': {
      // Deep-dish red wheel with a hub and nuts.
      lathe(
        [
          [rim, 0.44],
          [rim - 0.04, 0.42],
          [0.45, 0.22],
          [0.32, 0.2],
          [0.3, 0.32],
          [0.2, 0.36],
          [0, 0.36],
        ],
        { col: 0x7a1e14, rough: 0.55, metal: 0.25 },
      );
      nuts(8, 0.38, 0.25, 0.03);
      break;
    }
    default: {
      // Stand-in for a Blender rim until its pack is in (rimModels.ts): a plain dish in the design's finish, a dark cap.
      const s = MODERN[style]!;
      lathe([[rim, 0.44], [rim - 0.012, 0.475], [rim - 0.08, 0.42], [0.22, 0.34], [0.2, 0.38]], s);
      lathe([[0.2, 0.38], [0.08, 0.42], [0, 0.43]], { col: 0x1a1a1b, rough: 0.3, metal: 0.6 });
      break;
    }
  }
  g = kit.build();
  wheelCache.set(key, g);
  return g;
}

// ------------------------------------------------------------------------------------ anchors

/** Where body details (door seams, belt line, wheel arches) sit on a spec's mesh. */
export interface BodyAnchors {
  /** z of door shut lines, front to rear. */
  seamZ: number[];
  /** Height of the shoulder line (top of the painted flank). */
  beltY: number;
  clearance: number;
  /** Open z ranges along the flank that avoid the wheel arches (front-most first). */
  flank: [number, number][];
}

export function bodyAnchors(spec: VehicleSpec): BodyAnchors {
  const L = spec.length;
  const H = spec.height;
  if (spec.body === 'lorry') {
    const R = 0.52;
    const axleF = L / 2 - 1.15;
    const axleR1 = axleF - spec.wheelbase;
    const axleR2 = axleR1 - 1.2;
    const gap = R + 0.12;
    return {
      seamZ: [axleF + 0.55],
      beltY: H * 0.6,
      clearance: 0.45,
      flank: [
        [axleR1 + gap, axleF - gap],
        [-L / 2 + 0.15, axleR2 - gap],
      ],
    };
  }
  const p = DESIGNS[spec.body];
  const cz = p.cabin.map((c) => c[0] * L);
  const front = cz[cz.length - 1]!;
  const rear = cz[0]!;
  const archR = p.wheelR + 0.065 + 0.04;
  const axF = spec.wheelbase / 2;
  const axR = -spec.wheelbase / 2;
  return {
    seamZ: [front, (front + rear) / 2, rear],
    beltY: Math.min(p.cabin[0]![1], p.cabin[p.cabin.length - 1]![1]) * H,
    clearance: p.clearance,
    flank: [
      [axF + archR, (L / 2) * 0.97],
      [axR + archR, axF - archR],
      [(-L / 2) * 0.97, axR - archR],
    ],
  };
}

// ------------------------------------------------------------------------------------ shared pools

/** Radial gradient texture for fake light pools on the road (generated, no DOM needed). */
function radialTexture(): THREE.DataTexture {
  const n = 64;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = (x + 0.5) / n - 0.5;
      const dy = (y + 0.5) / n - 0.5;
      const d = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 2);
      const a = Math.pow(1 - d, 2.2);
      const i = (y * n + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(a * 255);
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}
const poolTex = radialTexture();
export const POOL_MATERIALS = {
  sirenBlue: new THREE.MeshBasicMaterial({ map: poolTex, color: 0x2a5cff, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false }),
  fire: new THREE.MeshBasicMaterial({ map: poolTex, color: 0xff7a20, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false }),
};
export const POOL_GEOMETRY = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
